/**
 * English strings. Every user-facing string lives here (spec §19.13); `{name}` marks a parameter.
 */
export const en = {
  'app.title': 'PAWNED',
  'app.tagline': 'A game of pawns, cards and collateral, played in the back room of a pawnshop.',
  'app.rulesVersion': 'Rules {version}',
  'app.version': 'Version {version}',
  'app.build': 'Version {version} · rules {rules} · build {commit}{date}',

  'menu.continueDuel': 'Continue duel',
  'menu.newDuel': 'New duel',
  'menu.campaign': "The Broker's tables",
  'menu.campaignHint': 'Campaign: three tables against the Broker, with pawns you can lose.',
  'menu.replays': 'Replays',
  'menu.rules': 'How to play',
  'menu.settings': 'Settings',
  'menu.back': 'Back',
  'menu.mainMenu': 'Main menu',
  'menu.resumeFailed': 'The saved duel could not be restored and was discarded.',
  'menu.online': 'Play online',
  'menu.onlineHint': 'Invite a friend with a link, or join their game.',
  'menu.leaderboard': 'Leaderboard',
  'menu.tutorial': 'Learn to play',
  'menu.tutorialHint': 'Interactive tutorial, about ten minutes',
  'menu.tutorialResume': 'Continue the tutorial',
  'menu.tutorialAgain': 'Tutorial',
  'menu.sharingNotice':
    'Matches you play are shared anonymously to help improve the game. You can turn this off in Settings.',

  'side.S': 'South',
  'side.N': 'North',
  'side.S.short': 'S',
  'side.N.short': 'N',

  'color.R': 'Red',
  'color.B': 'Blue',
  'color.G': 'Green',
  'color.Y': 'Yellow',
  'color.R.move': 'orthogonal slide',
  'color.B.move': 'diagonal slide',
  'color.G.move': 'knight jump',
  'color.Y.move': 'diagonal hops',
  'card.wild': 'Wild',
  'card.wildNamed': 'Wild named {face}',
  'card.epitaph': 'Epitaph',
  'card.label': '{color} {number}',
  'card.moveRed': 'Slide exactly {n} squares orthogonally.',
  'card.moveRed.one': 'Slide exactly 1 square orthogonally.',
  'card.moveBlue': 'Slide exactly {n} squares diagonally.',
  'card.moveBlue.one': 'Slide exactly 1 square diagonally.',
  'card.moveGreen': 'One knight jump (the number only matters for matching).',
  'card.moveYellow': 'Hop over pieces diagonally, 1 to {n} times.',
  'card.moveYellow.one': 'Hop over one piece diagonally.',
  'card.moveWild': 'Name any face. Costs a pawn as collateral for the rest of the round.',

  'setup.title': 'New duel',
  'setup.opponent': 'Opponent',
  'setup.opponent.ai': 'Computer',
  'setup.opponent.hotseat': 'Two players, one device',
  'setup.difficulty': 'Difficulty',
  'setup.difficulty.easy': 'Easy',
  'setup.difficulty.easy.hint': 'Plays for points and rarely thinks ahead.',
  'setup.difficulty.standard': 'Standard',
  'setup.difficulty.standard.hint': 'Weighs threats, Epitaphs and card economy.',
  'setup.difficulty.hard': 'Hard',
  'setup.difficulty.hard.hint': 'Searches deeper and samples more hidden hands.',
  'setup.side': 'Your side',
  'setup.side.random': 'Random',
  'setup.preset': 'Rules',
  'setup.preset.A': 'Standard (Preset A)',
  'setup.preset.A.hint': 'Pawns on their own home row are immune. Crowning scores 8 at once.',
  'setup.preset.B': 'Variant B',
  'setup.preset.B.hint':
    'No home-row immunity. Crown bonus is paid at your next turn if the pawn survives.',
  'setup.preset.BASELINE': 'Baseline',
  'setup.preset.BASELINE.hint': 'No home-row immunity. Crowning scores 8 at once.',
  'setup.names': 'Names',
  'setup.nameSouth': 'South player',
  'setup.nameNorth': 'North player',
  'setup.seed': 'Seed (optional)',
  'setup.seedHint': 'The same seed and actions always replay the same match.',
  'setup.start': 'Start duel',
  'setup.replaceSave': 'Starting a new duel replaces the duel in progress.',

  'match.round': 'Round {round} of {max}',
  'match.roundN': 'Round {round}',
  'match.preset': 'Rules: {preset}',
  'match.starter': '{side} started this round',
  'match.starterShort': '{side} started',
  'match.roundScore': 'Round score',
  'match.headStart': 'includes head start {n}',
  'match.total': 'Total',
  'match.roundWins': 'Round wins',
  'match.wins': 'Wins',
  'match.winsOf': '{n} of {of}',
  'match.hand': 'Hand',
  'match.deck': 'Deck',
  'match.handDeck': 'Hand {hand} · Deck {deck}',
  'match.passed': 'Passed',
  'match.epitaphPlayed': 'Epitaph played this round',
  'match.spent': 'Spent',
  'match.removed': 'Removed',
  'match.reserves': 'Reserves',
  'match.reserveUsed': 'used this round',
  'match.pile': 'Pile',
  'match.board': 'Board',
  'match.details': 'Details',
  'match.pileEmpty': 'Empty pile: any card may be played.',
  'match.top': 'Top',
  'match.viewPile': 'View the whole pile ({n})',
  'pile.epitaphEntry': 'Epitaph: {face}',
  'pile.epitaphTop': '{face} (Epitaph)',
  'pile.chalkEntry': 'Chalked: {face}',
  'pile.chalkTop': '{face} (chalked)',
  'match.log': 'Match log',
  'match.players': 'Players',
  'match.table': 'Deck, pile and what to do',
  'match.zones': 'Zones',
  'match.describe': 'Describe the position',
  'match.you': 'You',
  'match.thinking': '{name} is thinking…',
  'match.waiting': 'Waiting for {name}…',
  'match.concede': 'Concede',
  'match.concedeConfirm': 'Concede the match? {opponent} wins.',
  'match.menu': 'Menu',
  'bar.round': 'Round',
  'bar.log': 'Match log',
  'menu.symbolsHint': 'What the marks on pieces, squares and cards mean',
  'menu.logHint': 'Every move so far, in notation',
  'menu.describeHint': 'The whole position, as text',
  'menu.flipHint': 'See the board from the other side',
  'menu.rulesPreset': 'Rules: {preset}',
  'menu.customRules': 'Custom',
  'strip.thinking': 'Thinking',
  'strip.turn': 'To play',
  'strip.hasEpitaph': 'holds an Epitaph',
  'strip.details': 'Show details',
  'player.thisRound': 'This round',
  'deck.draw': 'Draw a card from your deck ({n} left)',
  'deck.label': 'Deck: {n} cards',
  'deck.empty': 'Empty',
  'deck.cue': 'Draw',
  'pile.label': 'Pile: {top} on top, {n} cards. Show the whole pile.',
  'pile.any': 'any card',
  'pile.anyShort': 'Any card',
  'info.formula': '{p} × {m} = {score}',
  'info.thisTurn': 'This turn',
  'info.crowns': 'Crowns',
  'info.collateral': 'Pawns {pawn}',
  'info.epitaphGift': 'Hands over an Epitaph',
  'info.nextTop': 'Next top {face}',
  'info.immune': 'immune',
  'match.saveExit': 'Save and exit',
  'match.exit': 'Exit to menu',
  'match.flip': 'Flip board',
  'match.noHand': 'No cards in hand.',
  'match.cardsHidden': '{n} hidden cards',

  'prompt.turnStart': 'Draw a card, or pass for the round.',
  'prompt.turnStartFull': 'Hand full: continue, or pass for the round.',
  'prompt.turnStartNoDeck': 'Deck empty: continue, or pass for the round.',
  'prompt.turnStartPlay': 'Play a matching card, or pass for the round.',
  'prompt.turnStartRecover': 'Recover a card, or pass for the round.',
  'prompt.turnStartStuck': 'No possible move: pass for the round.',
  'prompt.action': 'Play a card that matches the pile.',
  'prompt.onlyRecover': 'No card can move: Recover one.',
  'note.mustAct': 'You drew, so you must act: you can pass only before drawing.',
  'note.nothingMoves': 'No card can move now. If the one you draw can’t either, you must Recover.',
  'note.nothingMovesLast':
    'No card can move now, and one card is left to draw. If it can’t move either, there is no possible move: you must pass.',
  'note.stuck':
    'No card can move a pawn, no route can be replayed, and the deck is empty: nothing to draw or Recover.',
  'prompt.chain': 'Chain on with {pawn}, or end your turn.',
  'prompt.draftChain': 'Chain on with {pawn}, or end your turn.',
  'prompt.brazier': 'Brazier lit: any card goes on, and burns.',
  'hand.burns': 'Burns',
  'prompt.draftDone': 'Your move is set. End your turn, or undo it.',
  'prompt.sending': 'Ending your turn…',
  'prompt.whichPawn': 'More than one pawn can go there: tap the one to move.',
  'prompt.choosePawn': 'Tap a lit square to move a pawn there.',
  'prompt.chooseTarget': 'Choose where {pawn} goes.',
  'prompt.chooseHop': 'Choose the next hop, or stop here.',
  'prompt.chooseFace': 'Name the face the Wild counts as.',
  'prompt.chooseCollateral': 'Tap the pawn that pays for the Wild: it leaves for the round.',
  'prompt.chooseCollateralReserve': 'Pick the reserve below that pays for the Wild.',
  'prompt.chooseCollateralEither': 'Tap a pawn, or pick a reserve below, to pay for the Wild.',
  'hint.inTheDark': '{pawn} stands in the dark: it can neither move nor pay for a Wild.',
  'prompt.chooseTargetPawning': '{collateral} pays. Tap where {pawn} goes.',
  'prompt.recover': 'Pick a card to lose for a new draw.',
  'prompt.pickCard': 'Pick the card for this move.',
  'prompt.epitaph': 'Choose the pawn that replays your Epitaph.',
  'prompt.epitaphAgain': 'Tap it again to replay, or pick another pawn.',
  'prompt.starter': 'Choose who starts round {round}.',
  'prompt.inscription': 'Keep the inscription, or take the new one?',
  'dialog.inscription.body':
    '{pawn} already carries a route. Keep it, or inscribe the chain it just made instead? If the pawn is ever captured, its inscription comes back to you as an Epitaph.',
  'prompt.epitaphChoice': 'Choose the Epitaph to keep.',
  'prompt.gameOver': 'The match is over.',
  'lastTurn.title': 'Last turn',
  'lastTurn.links': '{name}: {moves}',
  'lastTurn.epitaph': '{name} replayed an Epitaph: {moves}',
  'lastTurn.score': 'scored {p} × {m} = {score}',
  'lastTurn.capture': '1 capture',
  'lastTurn.captures': '{n} captures',
  'lastTurn.crowned': 'crowned',
  'lastTurn.recovered': '{name} recovered {card}: it leaves the match.',
  'lastTurn.passed.voluntary': '{name} passed for the round.',
  'lastTurn.passed.forced': '{name} could not act, and passed.',
  'lastTurn.passed.automatic': '{name} had no pawns on the board, and passed.',
  'lastTurn.passed.outOfTurns': '{name} had used every turn of the round, and passed.',

  'action.pass': 'Pass',
  'action.continue': 'Continue',
  'action.endTurn': 'End turn',
  'action.undo': 'Undo',
  'action.undoLong': 'Undo the last move',
  'action.recover': 'Recover…',
  'action.playEpitaph': 'Replay Epitaph…',
  'action.playEpitaphShort': 'Epitaph…',
  'action.cancel': 'Cancel',
  'action.back': 'Back',
  'action.replay': 'Replay',
  'action.stopHere': 'Stop here',
  'action.confirm': 'Confirm',
  'action.keep': 'Keep',
  'action.keepThis': 'Keep this one',
  'action.replace': 'Replace',
  'action.close': 'Close',
  'action.next': 'Next',

  'reason.NO_MATCH': 'Does not match the top ({top}): needs the same color or number.',
  'reason.NO_LEGAL_MOVE': 'No legal move for any pawn that could play it.',
  'reason.NO_COLLATERAL': 'No pawn can be pawned to pay for this Wild.',
  'reason.NOT_IN_HAND': 'Not in your hand.',
  'reason.NOT_YOUR_TURN': 'It is not your decision.',
  'reason.TURN_SPENT': 'Nothing can follow your last link: end your turn.',
  'reason.short.NO_MATCH': 'No match',
  'reason.short.NO_LEGAL_MOVE': 'No move',
  'reason.short.NO_COLLATERAL': 'No collateral',
  'reason.short.NOT_IN_HAND': '—',
  'reason.short.NOT_YOUR_TURN': '—',
  'reason.short.TURN_SPENT': 'Turn over',
  'reason.wrongPhase': 'Cards can be played after you draw.',

  'preview.title': 'If you play this link',
  'preview.ifEnded': 'If the chain ends here',
  'preview.formula': '(P {p}) × (M {m}) = {score}',
  'preview.points': 'P = {landing} landing + {capture} capture + {crown} crown',
  'preview.multiplier': 'M = {links} links + {pattern} pattern + {fullCircle} Full Circle',
  'preview.pattern.ladder': 'Ladder',
  'preview.pattern.echo': 'Echo',
  'preview.pattern.streak': 'Streak',
  'preview.pattern.stair': 'Stair',
  'preview.pattern.prism': 'Prism',
  'preview.pattern.mirror': 'Mirror',
  'preview.pattern.none': 'none',
  'preview.fullCircle': 'Full Circle',
  'preview.captures': 'Captures {n}',
  'preview.crowns': 'Crowns the pawn',
  'preview.crownPending': 'Crown bonus pending until your next turn',
  'preview.nextTop': 'The opponent will face {face}',
  'preview.chainEnds': 'The chain ends automatically after this link.',
  'preview.epitaphGift': '{owner} will receive an Epitaph from {pawn}: {route} (scored {score}).',
  'preview.collateral': '{pawn} will be pawned for the rest of the round.',

  'wild.pickFace': 'Name a face',
  'wild.pickCollateral': 'Collateral',
  'wild.reserve': 'Reserve: {name}',
  'wild.collateralOn': '{pawn}, on {square}',

  'dir.forward': 'forward',
  'dir.back': 'back',
  'dir.left': 'left',
  'dir.right': 'right',
  'dir.diagonal': '{v}-{h}',
  'route.slide': '{n} squares {dir}',
  'route.slide.one': '1 square {dir}',
  'route.knight': 'L jump {dir}',
  'route.hop': '1 hop {dir}',
  'route.hops': '{n} hops',
  'route.knights': '{n} knight jumps',
  'route.still': 'a Trick (no move)',
  'route.step': '{face}, {move}',
  'route.then': '; then ',
  'epitaph.slot': 'Epitaph slot',
  'epitaph.empty': 'Empty',
  'epitaph.score': 'Recorded score {score}',
  'epitaph.cannot': 'Cannot be replayed by {pawn}: link {link} {reason}.',
  'epitaph.fail.OFF_BOARD': 'would leave the board',
  'epitaph.fail.BACKWARD': 'would move backward before crowning',
  'epitaph.fail.BLOCKED': 'is blocked',
  'epitaph.fail.OWN_PIECE': 'would land on your own pawn',
  'epitaph.fail.IMMUNE': 'would capture an immune pawn',
  'epitaph.fail.NOTHING_TO_JUMP': 'has no piece to jump',
  'epitaph.fail.LANDING_OCCUPIED': 'would land on an occupied square',
  'epitaph.fail.JUMPED_TWICE': 'would jump a piece twice',
  'epitaph.fail.WRONG_SHAPE': 'does not fit',
  'epitaph.fail.INVALID_PAWN': 'is not available',
  'epitaph.fail.IN_THE_DARK': 'would move in the dark',
  'epitaph.none': 'No pawn can replay it now.',

  'inscription.label': 'Inscription',
  'peek.label': 'Inscription of {pawn}',
  'peek.mine': 'Should it be captured, this route comes back to you as an Epitaph.',
  'peek.theirs': 'Capture it and {name} gets this route back as an Epitaph.',
  'peek.none': 'No inscription: capturing it gives no Epitaph.',
  'inscription.none': 'No inscription',
  'inscription.current': 'Current',
  'inscription.new': 'New chain',

  'pawn.label': '{name}, {side} pawn',
  'pawn.default': '{side} pawn {file}',
  'pawn.standInNamed': 'Stand-in {n}',
  'pawn.onSquare': 'the pawn on {square}',
  'pawn.crowned': 'crowned',
  'pawn.immune': 'immune on its home row',
  'pawn.inscribed': 'inscribed: {route} (scored {score})',
  'pawn.marked': 'marked with a pawn ticket: the Broker keeps it if you lose this table',
  'pawn.former': 'former pawn of yours',
  'pawn.standIn': 'stand-in',
  'hl.pawn': 'can make this link',
  'hl.move': 'legal destination',
  'hl.capture': 'legal capture',
  'hl.hop': 'next hop',
  'hl.epitaph': 'can replay the Epitaph',
  'hl.collateral': 'can be pawned',
  'hl.selected': 'selected',

  'legend.title': 'What the symbols mean',
  'legend.button': 'Symbols',
  'legend.pawns': 'Pawns',
  'legend.squares': 'Board highlights',
  'legend.cards': 'Cards and scores',
  'legend.south.title': 'South pawn.',
  'legend.south.text':
    'An orb of pale light. Pawns never move on their own: every move is made by playing a card.',
  'legend.north.title': 'North pawn.',
  'legend.north.text':
    'A crystal shard: pointed and faceted where South’s are round, so the sides never depend on color alone.',
  'legend.crowned.title': 'Ascended.',
  'legend.crowned.text':
    'A crowned pawn grows, brightens and wears a ring of gold light. It reached the enemy back row: it may move in any direction for the rest of the round. Crowning scores +8.',
  'legend.immune.title': 'Shield.',
  'legend.immune.text': 'Immune: a pawn on its own home row cannot be captured (standard rules).',
  'legend.inscription.title': 'Route badge.',
  'legend.inscription.text':
    'The pawn’s inscription: the route of a chain it made this round. If it is captured, its owner receives that route as an Epitaph.',
  'legend.ticket.title': 'Red pawn ticket (campaign).',
  'legend.ticket.text':
    'This pawn paid for a Wild at this table: it is marked. If you lose the table, the Broker keeps every marked pawn; win it and the tickets are torn up.',
  'legend.named.title': 'Name tag (campaign).',
  'legend.named.text': 'One of your named pawns: it keeps its name and record between runs.',
  'legend.hlPawn.title': 'Amber ring.',
  'legend.hlPawn.text': 'A pawn that can make the move you are building.',
  'legend.hlSelected.title': 'Amber glow.',
  'legend.hlSelected.text': 'The pawn you selected.',
  'legend.hlMove.title': 'Cyan light.',
  'legend.hlMove.text': 'A square the move can end on.',
  'legend.hlHop.title': 'Cyan diamond.',
  'legend.hlHop.text': 'The next hop of a Yellow move: hop on, or stop where you are.',
  'legend.hlCapture.title': 'Red ring with ×.',
  'legend.hlCapture.text': 'Ending here captures the enemy pawn: +5 points.',
  'legend.hlCollateral.title': 'Amber stripes.',
  'legend.hlCollateral.text':
    'A pawn you can pawn as collateral to pay for a Wild. It leaves the board for the round.',
  'legend.hlEpitaph.title': 'Green ring.',
  'legend.hlEpitaph.text': 'A pawn that can replay your Epitaph from where it stands.',
  'legend.hlLast.title': 'Violet haze.',
  'legend.hlLast.text':
    'Where the last turn went: every square of its route, with a trail of dots in the color of who played it. While you play, it follows your moves so far.',
  // The sea look draws other pieces and marks: its own words for them.
  'legend.sea.south.title': 'South pawn.',
  'legend.sea.south.text':
    'A scallop shell. Pawns never move on their own: every move is made by playing a card.',
  'legend.sea.north.title': 'North pawn.',
  'legend.sea.north.text':
    'A starfish: five pointed arms where South’s shells are fanned, so the sides never depend on color alone.',
  'legend.sea.crowned.title': 'Crowned.',
  'legend.sea.crowned.text':
    'A crowned pawn wears a gold crown set with pearls. It reached the enemy back row: it may move in any direction for the rest of the round. Crowning scores +8.',
  'legend.sea.hlMove.title': 'Teal dot.',
  'legend.sea.hlMove.text': 'A square the move can end on.',
  'legend.sea.hlHop.title': 'Teal diamond.',
  'legend.sea.hlHop.text': 'The next hop of a Yellow move: hop on, or stop where you are.',
  'legend.sea.hlEpitaph.title': 'Purple ring.',
  'legend.sea.hlEpitaph.text': 'A pawn that can replay your Epitaph from where it stands.',
  'legend.sea.hlLast.title': 'Golden haze.',
  'legend.sea.hlLast.text':
    'Where the last turn went: every square of its route, with a trail of dots in the color of who played it. While you play, it follows your moves so far.',
  'legend.card.title': 'Color, glyph and number.',
  'legend.card.text':
    'Red ✚ slides straight, Blue ✖ slides diagonally, Green L jumps like a knight, Yellow ● hops over pawns. The number, in the corners and as that many marks in the middle, is the distance (for Green it only matters for matching).',
  'legend.wild.title': 'Wild.',
  'legend.wild.text': 'Counts as any face you name. Playing it costs a pawn as collateral.',
  'legend.namedWild.title': 'Wild on the pile.',
  'legend.namedWild.text':
    'A dark card marked W, showing the face it was named, here Green 3: the next card must be Green or a 3.',
  'legend.epitaph.title': 'Epitaph.',
  'legend.epitaph.text':
    'The route of one of your captured pawns. Once per round, as a whole turn, any of your pawns may replay it, without matching the pile or spending cards.',
  'legend.unplayable.title': 'Dimmed card.',
  'legend.unplayable.text': 'It cannot be played right now; the reason is written across it.',
  'legend.wins.title': 'Dots by a name.',
  'legend.wins.text': 'Round wins. Two round wins take the match.',
  'legend.deck.title': 'Card back.',
  'legend.deck.text':
    'Your deck lies face down beside the pile. At the start of your turn, tap it to draw a card.',
  'legend.rounds.title': 'Round dots.',
  'legend.rounds.text':
    'At the top of the screen, one dot per round: the current one ringed, finished ones in the color of the side that won them.',
  'square.empty': '{square}, empty',
  'square.inTheDark': 'In the dark: nothing moves from it or onto it',
  'square.with': '{square}: {pawn}',

  'dialog.starter.title': 'Round {round}',
  'dialog.starter.body': 'You lost the last round, so you choose who starts this one.',
  'dialog.starter.me': 'I start',
  'dialog.starter.them': '{name} starts',
  'dialog.inscription.title': 'Inscription',
  'dialog.epitaph.title': 'Keep one Epitaph',
  'dialog.epitaph.arrived.one':
    '{opponent} captured one of your inscribed pawns, and its route came back to you as an Epitaph.',
  'dialog.epitaph.arrived.many':
    '{opponent} captured {n} of your inscribed pawns, and each route came back to you as an Epitaph.',
  'dialog.epitaph.slot': 'You can hold only one Epitaph: keep one, and the others are lost.',
  'dialog.epitaph.slotHeld':
    'You can hold only one Epitaph, and you already have one: keep one, and the other is lost.',
  'dialog.epitaph.slots': 'You can hold {n} Epitaphs: keep them one at a time; the rest are lost.',
  'dialog.epitaph.then': 'Then {opponent}’s turn goes on.',
  'dialog.epitaph.from': 'Left by your pawn captured on {square}',
  'dialog.epitaph.fromPawn': 'Left by {pawn}',
  'dialog.epitaph.held': 'The one you already hold',
  'dialog.epitaph.worth': 'Multiplier ×{m} when replayed · scored {score} when made',
  'dialog.epitaph.keepLabel': 'Keep the Epitaph: {origin}',
  'dialog.confirm.title': 'Confirm',
  'dialog.confirm.endTurn': 'End your turn? Its moves can no longer be undone.',
  'dialog.confirm.recover': 'Recover {card}? It leaves the match for good.',
  'dialog.roundEnd.title': 'Round {round} over',
  'dialog.roundEnd.winner': '{name} wins the round, {w} to {l}.',
  'dialog.roundEnd.tie': 'The round is tied at {s}. Nobody wins it.',
  'dialog.matchEnd.title': 'Match over',
  'dialog.matchEnd.winner': '{name} wins the match.',
  'dialog.matchEnd.draw': 'The match is a draw.',
  'dialog.matchEnd.reason.roundWins': 'Decided on round wins.',
  'dialog.matchEnd.reason.totalScore': 'Round wins were level; decided on total score.',
  'dialog.matchEnd.reason.draw': 'Round wins and totals are level.',
  'dialog.matchEnd.reason.brokerTie': 'An exact tie: the Broker wins ties.',
  'dialog.matchEnd.reason.concede': 'Decided by concession.',
  'dialog.matchEnd.saveReplay': 'Save replay',
  'dialog.matchEnd.saved': 'Replay saved.',
  'dialog.matchEnd.export': 'Export replay file',
  'dialog.matchEnd.rematch': 'Rematch',
  'dialog.noMove.title': 'No possible move',
  'dialog.noMove.lead': '{name} had to pass:',
  'dialog.noMove.drew': '{name} drew {card}, the last card of the deck.',
  'dialog.noMove.drewHidden': '{name} drew the last card of the deck.',
  'dialog.noMove.noCard': 'No card in hand can move a pawn, and no route can be replayed.',
  'dialog.noMove.noDeck': 'The deck is empty: nothing to draw or Recover.',
  'dialog.noMove.noPawns': 'No pawns are left on the board.',
  'dialog.noMove.titleTurns': 'Out of turns',
  'dialog.noMove.outOfTurns': '{rule} allows {n} turns a round, and {name} has had them all.',
  'dialog.noMove.playsOn': 'The round goes on: {name} plays every turn until passing too.',
  'dialog.noMove.roundOver': 'With both players out, round {round} is over.',
  'dialog.noMove.matchOver': 'With both players out, round {round} is over, and so is the match.',
  'dialog.noMove.result': 'See the result',
  'dialog.handoff.title': 'Pass the device',
  'dialog.handoff.body':
    'Hand the device to {name} ({side}). Their cards stay hidden until they are ready.',
  'dialog.handoff.ready': "I'm {name}: show my hand",

  'explain.automaticPass': 'No pawns left on the board: {name} had to pass.',
  'explain.forcedPass': 'No possible move: {name} had to pass.',
  'explain.outOfTurns': '{name} has had all {n} turns of the round ({rule}), and passes.',

  'log.matchStarted': 'The match begins. Rules {version}, preset {preset}.',
  'log.roundStarted': 'Round {round} begins.',
  'log.roundStartedHead': 'Round {round} begins. North starts with a head start of {n}.',
  'log.starter.random': '{side} starts round {round} (drawn at random).',
  'log.starter.loserChoice': '{side} starts round {round} ({chooser} chose).',
  'log.starter.tieRule': '{side} starts round {round} (the last round was tied).',
  'log.passed.voluntary': '{side} passes.',
  'log.passed.automatic': '{side} passes automatically: no pawns on the board.',
  'log.passed.forced': '{side} must pass: no legal action.',
  'log.passed.outOfTurns': '{side} is out of turns for the round, and passes.',
  'log.passed.outOfTurnsRule': '{side} has had all {n} turns ({rule}), and passes.',
  'log.drawn': '{side} draws {card}.',
  'log.drawnHidden': '{side} draws a card.',
  'log.link': '{side}: {notation}',
  'log.linkCollateral': '{side}: {notation}, pawning {pawn}',
  'log.linkReserve': '{side}: {notation}, pawning reserve {pawn}',
  'log.captured': '{pawn} is captured on {square}.',
  'log.capturedEpitaph': '{pawn} is captured on {square}, leaving an Epitaph.',
  'log.crowned': '{pawn} is crowned on {square}.',
  'log.scored': '{side} scores (P {p}) × (M {m}) = {score}{extra}.',
  'log.scoredEpitaph': "{side}'s Epitaph scores (P {p}) × (M {m}) = {score}{extra}.",
  'log.inscription.automatic': '{pawn} is inscribed: {route}.',
  'log.inscription.kept': '{pawn} keeps its inscription: {route}.',
  'log.inscription.replaced': '{pawn} is re-inscribed: {route}.',
  'log.epitaphReceived': '{side} takes an Epitaph: {route}.',
  'log.epitaphDiscarded': '{side} discards an Epitaph: {route}.',
  'log.inscription.veiled': '{pawn} is inscribed; Veiled, the route is hidden from you.',
  'log.epitaphReceivedVeiled': '{side} takes an Epitaph; Veiled, its route is hidden from you.',
  'log.epitaphDiscardedVeiled': '{side} discards an Epitaph; Veiled, its route is hidden.',
  'epitaph.veiled': 'Veiled: its route is hidden from you.',
  'peek.veiled': 'Veiled: whatever route this pawn carries is hidden from you.',
  'log.epitaphPlayed': '{side} replays an Epitaph with {pawn}: {route}.',
  'log.recovered': '{side} recovers {card}: it leaves the match.',
  'log.crownPending': 'A crown bonus of {n} waits on {pawn}.',
  'log.crownAwarded': '{side} gains a crown bonus of {n} for {pawn}.',
  'log.crownDiscarded': 'The crown bonus for {pawn} is lost.',
  'log.roundEnded': 'Round {round} ends: South {s}, North {n}. {winner} wins.',
  'log.roundTied': 'Round {round} ends: South {s}, North {n}. Tied.',
  'log.matchEnded': 'The match ends: {winner} wins.',
  'log.matchDrawn': 'The match ends in a draw.',
  'log.conceded': '{side} concedes.',
  'log.extra.pattern': ', {pattern}',
  'log.extra.fullCircle': ', Full Circle',

  'score.chain': 'Chain',
  'score.epitaph': 'Epitaph',
  'score.step.landing': 'landing',
  'score.step.capture': 'capture',
  'score.step.crown': 'crown',
  'score.step.link': 'link',
  'tally.record': 'Best chain',
  'tally.inkOut': 'the ink ran out',
  'score.skip': 'Tap to skip',

  'campaign.title': "The Broker's tables",
  'campaign.intro':
    'Win three tables in a row against the regulars of the Broker. Every pawn you pawn for a Wild is marked; lose a table and the Broker keeps your marked pawns.',
  'campaign.stable': 'Your stable',
  'campaign.collection': "The Broker's collection",
  'campaign.collectionEmpty': 'The Broker holds none of your pawns.',
  'campaign.stats': 'Runs {started} · won {won} · tables won {tables}',
  'campaign.newRun': 'Start a run',
  'campaign.continueRun': 'Continue the run',
  'campaign.abandonRun': 'Abandon the run',
  'campaign.abandonConfirm': 'Abandon this run? It counts as lost; unmarked pawns are safe.',
  'campaign.resetProfile': 'Start a new profile',
  'campaign.resetConfirm': 'Erase the profile, its stable and its history?',
  'campaign.chooseRoster': 'Choose {n} pawns for this run',
  'campaign.chooseRosterStandIns': 'Choose your {n} pawns; {m} nameless stand-ins fill the gaps.',
  'campaign.table': 'Table {table} of {tables}',
  'campaign.regular': 'Regular: {name}',
  'campaign.headStart': 'Head start for the Broker: {n} points every round',
  'campaign.field': 'Field your pawns',
  'campaign.fieldHint':
    'Put six roster pawns on files A–F. The rest wait as reserves and can pay for Wilds.',
  'campaign.fieldAuto': 'Fill in order',
  'campaign.file': 'File {file}',
  'campaign.reserve': 'Reserve',
  'campaign.play': 'Sit down at the table',
  'campaign.deck': 'Run deck: {n} cards',
  'campaign.bought': 'Bought this run: {cards}',
  'campaign.tableScore': 'Total score: you {s}, the Broker {n}.',
  'campaign.history': 'Recent runs',
  'campaign.historyItem': 'Run {n}: {outcome}, {tables} tables won',
  'campaign.history.won': 'won',
  'campaign.history.lost': 'lost',
  'campaign.history.abandoned': 'abandoned',
  'campaign.won.title': 'Table won',
  'campaign.lost.title': 'Table lost',
  'campaign.reclaimed': 'Reclaimed from the Broker: {names}',
  'campaign.recruited': 'Recruited: {names}',
  'campaign.lostPawns': 'The Broker keeps: {names}',
  'campaign.vanished': 'Marked stand-ins vanished: {n}',
  'campaign.noLosses': 'No pawn was marked; the Broker takes nothing.',
  'campaign.counter': 'The Counter',
  'campaign.counterHint':
    'Buy any offered card for one pawn from your roster. Traded pawns join the Broker’s collection. Keep at least {n} pawns.',
  'campaign.payWith': 'Pay with',
  'campaign.dontBuy': 'Not buying',
  'campaign.leaveCounter': 'Leave the Counter',
  'campaign.runWon': 'The run is won. The Broker slides the chips across the table.',
  'campaign.runLost': 'The run is over.',
  'campaign.recap.won': 'Table {table}, {name}: won, {s} to {n}',
  'campaign.recap.lost': 'Table {table}, {name}: lost, {s} to {n}',
  'campaign.closeRun': 'Back to the stable',
  'campaign.records':
    'Tables {tables} · captures {captures} · captured {captured} · Epitaphs left {epitaphs} · best chain {best}',
  'campaign.standIn': 'Stand-in',
  'campaign.brokerWinsTies': 'The Broker wins exact ties.',
  'campaign.howItWorks': 'How the campaign works',
  'campaign.ticketTip':
    'The red pawn ticket marks a pawn that paid for a Wild at this table. Lose this table and the Broker keeps every marked pawn; win it and the tickets are torn up.',
  'campaign.ticketTipDismiss': 'Got it',

  'campaignGuide.title': 'The Broker’s tables',
  'campaignGuide.step': 'Step {n} of {total}',
  'campaignGuide.back': 'Back',
  'campaignGuide.next': 'Next',
  'campaignGuide.done': 'Start playing',
  'campaignGuide.1.title': 'Three tables, one run',
  'campaignGuide.1.text':
    'A run is up to three matches in a row, one per table, each against one of the Broker’s regulars. Win all three tables to win the run. Lose one and the run is over.',
  'campaignGuide.2.title': 'The house starts ahead',
  'campaignGuide.2.text':
    'The regular starts every round with a head start: 0 points at table 1, 20 at table 2 and 50 at table 3. The Broker also wins exact ties.',
  'campaignGuide.3.title': 'Your stable',
  'campaignGuide.3.text':
    'You own named pawns. They stay with you between runs and keep a record of their deeds. Before each table you field six of them on files A–F; the rest of your roster waits as reserves. If you ever have fewer than six, nameless stand-ins fill the gaps.',
  'campaignGuide.4.title': 'Wilds cost pawns',
  'campaignGuide.4.text':
    'A Wild needs collateral: one of your pawns on the board, or a reserve. That pawn gets a red pawn ticket: it is marked until the table ends.',
  'campaignGuide.5.title': 'Lose a table, lose the marked',
  'campaignGuide.5.text':
    'If you lose a table, the run ends and the Broker keeps every marked pawn for his collection. Unmarked pawns are safe. Every Wild is a bet.',
  'campaignGuide.6.title': 'Win a table, grow stronger',
  'campaignGuide.6.text':
    'Win a table and the tickets are torn up. A new pawn joins your stable, and at the Counter you may buy the cards on offer, paying one reserve pawn for each. Your deck is reset for the next table.',
  'campaignGuide.7.title': 'Old friends across the table',
  'campaignGuide.7.text':
    'Pawns the Broker keeps may sit on his side at later tables, up to two at a time. Win that table to bring them home.',

  'share.match': 'Share a link to this match',
  'share.replay': 'Share a replay link',
  'share.linkLabel': 'Link to share',
  'share.copy': 'Copy',
  'share.copied': 'Copied',
  'share.share': 'Share…',
  'share.making': 'Making the link…',
  'share.failed': 'The link could not be made ({message}).',

  'online.title': 'Play online',
  'online.intro':
    'Play a friend anywhere: create a game and send them the link. Anyone else who opens the link can watch.',
  'online.unavailable': 'Online play is not available on this site right now.',
  'online.checking': 'Checking the tables…',
  'online.name': 'Your name',
  'online.nameHint': 'Shown to your opponent and on the leaderboard.',
  'online.nameInvalid': 'Use 1 to 24 letters, digits or spaces.',
  'online.nameScripts': 'Use the letters of one alphabet.',
  'online.nameReserved': 'That name belongs to the game. Choose another.',
  'online.nameOffensive': 'That name is not allowed. Choose another.',
  'online.nameTaken': 'Another player has that name, or one that looks like it. Choose another.',
  'online.preset': 'Rules',
  'online.create': 'Create a game',
  'online.creating': 'Setting up the table…',
  'online.createFailed': 'The game could not be created ({message}).',
  'online.join': 'Join a game',
  'online.joinLabel': 'Link or code',
  'online.joinHint': 'Paste the link your friend sent you, or its 10-letter code.',
  'online.joinInvalid': 'That is not a PAWNED game link or code.',
  'online.joinButton': 'Join',
  'online.rated':
    'Every finished online game counts for the leaderboard: everyone starts at 1200, and beating a stronger player gains more.',
  'online.yourRating':
    'Your rating: {rating}, rank {rank} ({wins} won, {losses} lost, {draws} drawn).',
  'online.unrated': 'You have no rating yet: finish an online game to get one.',

  'room.connecting': 'Connecting to the table…',
  'room.reconnecting': 'Connection lost. Reconnecting…',
  'room.notFound': 'This game does not exist, or it has expired.',
  'room.unavailable': 'The game server cannot be reached right now.',
  'room.waitingTitle': 'Waiting for your opponent',
  'room.waitingBody':
    'Send this link to the person you want to play. The game starts as soon as they open it.',
  'room.waitingRematch': 'The rematch starts as soon as {name} joins it.',
  'room.leave': 'Leave the table',
  'room.spectating': 'You are watching this game.',
  'room.title': 'Online · {a} vs {b}',
  'room.away': '{name} has left the table.',
  'room.awayWait':
    '{name} has left the table. If they are not back, you can claim the game in {seconds} s.',
  'room.claim': 'Claim the game',
  'room.rating': 'Rating: {before} → {after} ({delta})',
  'room.rematch': 'Rematch',
  'room.rematchAsked': 'Asking for a rematch…',
  'room.rematchOffered': '{name} wants a rematch.',
  'room.rematchReady': 'Your rematch is ready.',
  'room.rematchJoin': 'Join the rematch',
  'online.identityNotSaved':
    'This browser could not save who you are online ({message}). You can play now, but once this page is closed you cannot take your seat back or keep your rating. Free some space, or allow this site to store data, then reload.',
  'room.watchReplay': 'Watch the replay',
  'room.nameFirst': 'Choose a name to join this game',
  'room.join': 'Join the table',
  'room.sharedTitle': 'Shared match',

  'leaderboard.title': 'Leaderboard',
  'leaderboard.intro':
    'Ratings from finished online games. Everyone starts at 1200; beating a stronger player gains more.',
  'leaderboard.empty': 'No rated games yet. Play online to be the first!',
  'leaderboard.unavailable': 'The leaderboard is not available right now.',
  'leaderboard.loading': 'Loading…',
  'leaderboard.rank': 'Rank',
  'leaderboard.player': 'Player',
  'leaderboard.rating': 'Rating',
  'leaderboard.games': 'Games',
  'leaderboard.record': 'Won–lost–drawn',
  'leaderboard.hiddenName': 'Name hidden',
  'leaderboard.you': 'You',

  'replays.title': 'Replays',
  'replays.empty': 'No saved replays yet. Finish a match and save its replay.',
  'replays.import': 'Import replay file',
  'replays.importError': 'That file is not a PAWNED replay for rules {version}.',
  'replays.open': 'Watch',
  'replays.delete': 'Delete',
  'replays.export': 'Export',
  'replays.step': 'Action {i} of {n}',
  'replays.unplayable': 'This replay cannot be shown: its match cannot be set up ({reason}).',
  'replays.first': 'First',
  'replays.prev': 'Previous',
  'replays.nextStep': 'Next',
  'replays.last': 'Last',
  'replays.perspective': 'Perspective',
  'replays.omniscient': 'Everything',

  'app.install': 'Install the app',
  'app.fullscreen': 'Full screen',
  'app.leaveFullscreen': 'Leave full screen',
  'app.fullscreenHint': 'The game without the browser’s bars',
  'app.homeScreenHint':
    'To play full screen on an iPhone or iPad: tap Share, then Add to Home Screen, and open PAWNED from there.',
  'app.updated': 'PAWNED has been updated to {version}.',
  'settings.title': 'Settings',
  'settings.theme': 'Look',
  'settings.theme.void': 'The void',
  'settings.theme.void.hint': 'Violet smoke and starlight; orbs against crystal shards.',
  'settings.theme.sea': 'The sea',
  'settings.theme.sea.hint': 'Sunlit shallows and a sea-glass board; shells against starfish.',
  'settings.confirmActions': 'Ask before ending a turn and before a Recover',
  'settings.reducedMotion': 'Reduce animations',
  'settings.sound': 'Sound',
  'settings.volume': 'Volume',
  'settings.inscriptions': 'When a pawn already carries a route',
  'settings.inscriptionsHint':
    'At the end of a chain by an inscribed pawn: choose each time, or let the game choose for you.',
  'settings.inscriptions.ask': 'Ask me',
  'settings.inscriptions.keep': 'Keep the old route',
  'settings.inscriptions.better': 'Keep the better score',
  'notice.inscriptionKept': '{pawn} keeps its old route ({rule}).',
  'notice.inscriptionReplaced': '{pawn} takes the new route ({rule}).',
  'settings.aiSpeed': 'Game speed',
  'settings.aiSpeedHint':
    'How fast the computer plays, and how fast scores are counted. A tap on a count skips it.',
  'prompt.counting': 'Counting the score of {name}…',
  'prompt.countingYours': 'Counting your score…',
  'score.skipButton': 'Skip',
  'settings.aiSpeed.fast': 'Fast',
  'settings.aiSpeed.normal': 'Normal',
  'settings.aiSpeed.slow': 'Slow',
  'settings.flipHotseat': 'In two-player games, turn the board toward the player to act',
  'settings.showPreview': 'Show the live score preview',
  'settings.telemetry': 'Also keep playtest statistics on this device',
  'settings.telemetryHint':
    'Separate from sharing: these statistics stay in this browser until you export them.',
  'settings.shareMatches': 'Share my matches to help improve the game',
  'settings.shareStatus': '{n} matches shared from this browser, the last on {when}.',
  'settings.shareStatusOne': '1 match shared from this browser, on {when}.',
  'settings.shareWaiting': '{n} waiting to be sent: they go out on your next visit.',
  'settings.shareMatchesHint':
    'Each match is sent anonymously as you play it: the moves, scores and game version, with a random id for this browser. Nothing else about you.',
  'settings.exportTelemetry': 'Export telemetry ({n} matches)',
  'settings.clearTelemetry': 'Clear telemetry',
  'settings.highContrast': 'High-contrast board',
  'settings.about': 'About this build',
  'settings.storageUnavailable':
    'This browser does not allow saving; progress will be lost when the page closes.',

  'rules.title': 'How to play',
  'rules.tutorial':
    'Rather learn by playing? The tutorial walks you through every rule on the board.',
  'rules.intro':
    'PAWNED is a duel on a 6×6 board. Each side has six identical pawns that cannot move on their own: every move is made by playing a card, and every card must match the top of one shared discard pile. A card’s color decides how a pawn moves; its number decides how far.',
  'rules.goal.title': 'Winning',
  'rules.goal.body':
    'A match is up to three rounds. Win two rounds to win the match. A round ends when both sides have passed; the higher round score wins it. After three rounds, more round wins decide, then total score; if both are level, a duel is drawn (in the campaign, the Broker wins exact ties).\n\nEach side has one 26-card deck for the whole match: two of each of the twelve faces (red, blue, green and yellow, numbered 1 to 3) and two Wilds. Both sides draw seven cards at the start of the match, and there is no refill between rounds: every card spent in one round is gone for the next.\n\nThe starter of round 1 is drawn at random. After that, the loser of the previous round chooses who starts the next one; after a tied round, the side that did not start it starts.',
  'rules.board.title': 'Board and directions',
  'rules.board.body':
    'South starts on rank 1 and North on rank 6, one pawn per file. Forward means toward the enemy back row. A pawn that is not crowned may never move backward; sideways is allowed.\n\nReaching the enemy back row crowns a pawn: it may move in any direction for the rest of the round.\n\nHome-row rule (standard rules): a pawn standing on its own home row is immune and cannot be captured.',
  'rules.cards.title': 'Cards and movement',
  'rules.cards.body':
    '• Red ✚ slides straight (forward or sideways) exactly its number of squares. Pieces in between block it.\n• Blue ✖ slides diagonally exactly its number of squares. Pieces in between block it.\n• Green (knight L) makes one knight jump over anything. Its number only matters for matching.\n• Yellow ● hops diagonally over an adjacent piece onto the empty square beyond, 1 to its number of times. Jumped enemy pawns are captured at once; your own are only jumped. No piece can be jumped twice by one card.\n\nLanding on an enemy pawn with Red, Blue or Green captures it. You can never land on your own pawn.',
  'rules.matching.title': 'Matching the pile',
  'rules.matching.body':
    'Every card you play must share a color or a number with the top of the pile. Any card may start an empty pile, and a Wild may be played on anything. The card you play becomes the new top, so each move also limits what your opponent can play next.',
  'rules.turn.title': 'Your turn',
  'rules.turn.body':
    'At the start of your turn, draw or pass. Passing takes you out of the round: your opponent then plays every remaining turn until they pass too. Otherwise you draw the top card of your deck (if you hold fewer than seven), and then you must act: once you have drawn you can no longer pass this turn, even if none of your cards can move.\n\n• Chain: play one to three cards that all move the same pawn, each matching the top left by the one before.\n• Epitaph: replay a route you hold (see below).\n• Recover: remove a card in your hand from the match for good and draw a replacement.\n\nIf you have no pawns on the board you pass automatically; if you can do nothing at all you must pass.',
  'rules.score.title': 'Scoring: P × M',
  'rules.score.body':
    'A chain scores its points P times its multiplier M.\n\nP: +1 for every landing square not yet visited in the chain, +5 for every capture, +8 for crowning.\n\nM: one per link, plus the single best pattern: Ladder +2 (three links of one color numbered 1, 2, 3 in order), Echo +1 (two or more links, all the same number) or Streak +1 (two or more links, all the same color). Full Circle adds +2 when the pawn ends where it started without the route being a straight line. A Wild counts as the face you named for it.',
  'rules.wild.title': 'Wilds and collateral',
  'rules.wild.body':
    'A Wild counts as any face you name. To play it you must pawn another of your pawns as collateral: it leaves the board for the rest of the round (scoring nothing and leaving no Epitaph), which can also open a path for the move.',
  'rules.epitaph.title': 'Inscriptions and Epitaphs',
  'rules.epitaph.body':
    'The first chain a pawn makes in a round is inscribed on it; after later chains you may keep the old inscription or replace it. Inscriptions are public: hold a pawn (or right-click it) to see its own.\n\nWhen an inscribed pawn is captured, its owner receives the route as an Epitaph. Once per round, as a whole turn, any of your pawns may replay it from where it stands, without matching the pile and without spending cards. Every step must be legal as recorded; steps that were Wilds replay with the face they were given and need no collateral. The Epitaph then goes on the pile showing the face of its last step, which becomes the new top. A replay never creates or changes an inscription.\n\nYou can hold one Epitaph, and it stays in its slot from round to round until you play it. When more arrive, keep the best.\n\nSo think twice before capturing an inscribed pawn: you may be handing your opponent a free move.',
  'rules.variants.title': 'Rule presets',
  'rules.variants.body':
    '• Standard (A): home-row immunity on; crowning scores 8 at once.\n• Variant B: no immunity; the crown bonus of 8 is paid at your next turn if the pawn is still on the board (at once if your opponent has passed).\n• Baseline: no immunity; crowning scores 8 at once.',
  'rules.campaign.title': 'The Broker’s tables',
  'rules.campaign.body':
    'In the campaign you play three tables in a row against the Broker’s regulars, who start every round with a head start. You own a stable of named pawns. Any pawn you pawn for a Wild is marked; lose a table and the Broker keeps every marked pawn. Win and you reclaim your former pawns who sat with the Broker, recruit a new pawn, and may buy cards at the Counter by trading pawns.',
  'rules.symbols.title': 'What the symbols mean',
  'rules.controls.title': 'Controls',
  'rules.controls.body':
    'Everything works with touch, mouse or keyboard. A move happens as soon as you pick where it lands, or drag a pawn or a card to its square: Undo takes it back, End turn makes the turn final. Keys: 1–9 pick a card, arrow keys move around the board, Enter or Space selects, D (or C) draws, P passes, Z (or Backspace) undoes, E ends the turn, R starts a Recover, T replays your Epitaph, Escape cancels. Hold a pawn, or right-click it, to see its Inscription (I with the keyboard): while an Epitaph is being replayed, also where that pawn would replay it.',

  'tutorial.title': 'Learn to play',
  'tutorial.lesson': 'Lesson {n} of {total}',
  'tutorial.progress': 'Tutorial progress',
  'tutorial.task': 'Your move',
  'tutorial.hint': 'Hint',
  'tutorial.hideHint': 'Hide hint',
  'tutorial.next': 'Next lesson',
  'tutorial.finishLessons': 'Finish',
  'tutorial.tryAgain': 'Try again',
  'tutorial.restart': 'Restart lesson',
  'tutorial.previous': 'Previous lesson',
  'tutorial.skip': 'Skip lesson',
  'tutorial.leave': 'Leave the tutorial',
  'tutorial.welldone': 'Well done!',
  'tutorial.notQuite': 'Not quite',
  'tutorial.lockedDone': 'Lesson complete.',
  'tutorial.lockedRetry': 'Let’s try that again.',
  'tutorial.north': 'North',

  'tutorial.turn.title': 'Your turn',
  'tutorial.turn.body':
    'PAWNED is a duel on a 6×6 board. You play South, at the bottom; North sits at the top. Your pawns never move on their own: every move is made by playing a card from your hand.\n\nEvery turn starts with a choice. Draw a card from your deck (if you hold fewer than seven), and then you must act. Or pass, and sit out the rest of the round.\n\nIn these lessons North has already passed, so you can practise on your own.',
  'tutorial.turn.task': 'Tap your deck to draw a card and start your turn.',
  'tutorial.turn.hint':
    'Your deck is the face-down stack under the board, beside the pile. You can also press D.',
  'tutorial.turn.done':
    'You drew a card, and now you must act. Next: the four colors, each of which moves a pawn in its own way.',
  'tutorial.turn.retry':
    'Passing ends your part of the round, so you would not move at all. Draw a card from your deck instead.',

  'tutorial.red.title': 'Red: straight slides',
  'tutorial.red.body':
    'A card’s color decides how a pawn moves, and its number how far. Red ✚ slides a pawn straight, forward or sideways, exactly that many squares. Any piece in between blocks it.\n\nPawns never move backward until they are crowned; you will see how later.\n\nA move happens as soon as you tap where it lands. Undo takes it back; End turn makes it final.',
  'tutorial.red.task':
    'Play the Red 2: pick the card, then where a pawn lands. Then end your turn.',
  'tutorial.red.hint': 'Pick the Red 2, then your pawn on C1, then C3. Then press End turn.',
  'tutorial.red.done':
    'That scored {score}: one point for each new square landed on. Chains, captures and crowns score much more, as you will see.',
  'tutorial.red.retry': 'Play the Red 2.',

  'tutorial.blue.title': 'Blue: diagonal slides',
  'tutorial.blue.body':
    'Blue ✖ slides a pawn diagonally, exactly its number of squares. Like Red, it is blocked by any piece in between.\n\nLook at your pawn on C1: toward the upper left, your own pawn on B2 is in the way.',
  'tutorial.blue.task': 'Play the Blue 2 to slide a pawn two squares diagonally.',
  'tutorial.blue.hint': 'Pick the Blue 2, then your pawn on C1, then E3. Then press End turn.',
  'tutorial.blue.done':
    'The pawn on C1 could only go up and to the right: B2 blocked the other diagonal.',
  'tutorial.blue.retry': 'Play the Blue 2.',

  'tutorial.green.title': 'Green: knight jumps',
  'tutorial.green.body':
    'Green makes one knight jump, the L shape from chess: two squares one way and one square to the side. It leaps over anything in between, so it is never blocked.\n\nThe number on a Green card does not change the jump. It only matters for matching and for patterns.',
  'tutorial.green.task': 'Jump one of your pawns with the Green 1.',
  'tutorial.green.hint':
    'Pick the Green 1, then your pawn on B1, then C3: it leaps over the pawns on B2 and C2. Then press End turn.',
  'tutorial.green.done':
    'Knight jumps ignore whatever stands in between. Green is the way out of a crowded position.',
  'tutorial.green.retry': 'Play the Green 1.',

  'tutorial.yellow.title': 'Yellow: hops and captures',
  'tutorial.yellow.body':
    'Yellow ● hops like checkers: diagonally over the piece next to it, onto the empty square beyond. A Yellow card allows up to its number of hops, and you choose where to stop.\n\nAn enemy pawn you hop over is captured on the spot, for 5 points. You can hop over your own pawns too; they stay where they are.',
  'tutorial.yellow.task': 'Capture both North pawns with the Yellow 2: hop twice.',
  'tutorial.yellow.hint':
    'Pick the Yellow 2, then your pawn on B1. Hop to D3, then on to F5, and press End turn.',
  'tutorial.yellow.done':
    'Two captures with one card: {score} points. Captured pawns stay off the board until the round ends.',
  'tutorial.yellow.retry':
    'You stopped after one hop. The Yellow 2 allows a second hop: capture both pawns.',

  'tutorial.capture.title': 'Captures and safe squares',
  'tutorial.capture.body':
    'Red, Blue and Green capture by landing on an enemy pawn, for 5 points.\n\nA pawn on its own home row is immune: it cannot be captured there. North’s pawns on rank 6 are safe, and so are yours on rank 1. Once a pawn leaves home, it can be taken.',
  'tutorial.capture.task': 'Capture a North pawn with the Red 2.',
  'tutorial.capture.hint':
    'Pick the Red 2, then your pawn on C2, then the North pawn on C4, and press End turn. The pawn on E6 is on its home row, so it can’t be taken.',
  'tutorial.capture.done': 'Captured: {score} points, 1 for the square and 5 for the capture.',
  'tutorial.capture.retry': 'That move didn’t capture anything. Land on the North pawn on C4.',

  'tutorial.match.title': 'Matching the top',
  'tutorial.match.body':
    'Every card goes onto one shared pile, and each card you play must match the top card: the same color or the same number. The top is the last card played, by either side. An empty pile accepts anything, and a Wild goes on anything.\n\nThe top is Blue 2 now. Cards you can’t play are dimmed; pick one to see why.',
  'tutorial.match.task': 'Play a card that matches the Blue 2.',
  'tutorial.match.hint':
    'The Green 2 matches by number and the Blue 3 by color. The Red 1 and the Yellow 1 match neither. Play one, then press End turn.',
  'tutorial.match.done':
    'Matched. The card you finish on becomes the top your opponent has to match, so choose it with care.',
  'tutorial.match.retry': 'Play a card that matches the Blue 2.',

  'tutorial.chain.title': 'Chains multiply',
  'tutorial.chain.body':
    'One action can be a chain: up to three cards in a row, all moving the same pawn, each matching the card before it.\n\nA chain scores P × M. P is 1 for each new square landed on, 5 per capture and 8 for crowning. M is the number of cards, plus the best pattern: a Ladder (one color, played 1, 2, 3) adds 2; an Echo (all the same number) or a Streak (all the same color) adds 1.',
  'tutorial.chain.task': 'Play all three Reds as one chain on the same pawn, in the order 1, 2, 3.',
  'tutorial.chain.hint':
    'Pick the Red 1 and move your pawn on B1 to B2. Then pick the Red 2 and move it to B4, then the Red 3 and move it to E4. Then press End turn.',
  'tutorial.chain.done':
    'A three-card chain: {score} points. Played in the order 1, 2, 3 it would have been a Ladder, worth even more.',
  'tutorial.chain.donePlus':
    'A Ladder! 3 new squares × (3 cards + 2) = {score} points, from three cards that would score 1 each on their own.',
  'tutorial.chain.retry':
    'The chain ended before the third card. After each link, pick the next Red instead of ending your turn.',

  'tutorial.crown.title': 'Crowning',
  'tutorial.crown.body':
    'A pawn that reaches the far row (rank 6 for you) is crowned: it scores 8 points, and for the rest of the round it may move in any direction, backward too.\n\nA crowned pawn can even finish its chain back on the square where the chain began, after a detour that is not one straight line (out and straight back does not count): that is a Full Circle, worth 2 more on the multiplier.',
  'tutorial.crown.task': 'Crown your pawn on D4 with the Red 2, then chain the Red 1 to step back.',
  'tutorial.crown.hint':
    'Pick the Red 2 and move the pawn from D4 to D6. Then pick the Red 1 and move it back to D5, and press End turn.',
  'tutorial.crown.done': 'Crowned! That chain scored {score} points.',
  'tutorial.crown.donePlus':
    'Crowned, then back a step: (2 squares + 8 for the crown) × (2 cards + 1 for the Streak) = {score} points.',
  'tutorial.crown.retry': 'The pawn didn’t reach rank 6. Start with the Red 2, from D4 to D6.',

  'tutorial.wild.title': 'Wilds and collateral',
  'tutorial.wild.body':
    'A Wild plays as any face you name, any color and any number, on any top. It has a price: you must pawn one of your other pawns as collateral, and it leaves the board for the rest of the round. Pawning is not a capture, so it scores nothing for anyone.\n\nNone of your other cards matches the Red 3 on top.',
  'tutorial.wild.task':
    'Play the Wild as a Green 3 to jump from C2 onto the North pawn on D4, pawning one of your pawns.',
  'tutorial.wild.hint':
    'Pick the Wild, then your pawn on C2. Name Green 3, tap your pawn on E1 to pay for it (Back takes it off again), land on D4, and press End turn.',
  'tutorial.wild.done': 'The Wild got you moving, for the price of a pawn: {score} points.',
  'tutorial.wild.donePlus':
    'A capture no card in your hand could make: {score} points, for one pawn of collateral.',
  'tutorial.wild.retry': 'Play the Wild card.',

  'tutorial.epitaph.title': 'Inscriptions and Epitaphs',
  'tutorial.epitaph.body':
    'When a chain ends, its route is inscribed on the pawn that moved. If that pawn is captured later, its owner gets the route back as an Epitaph. Hold a finger on any pawn (or right-click it) to see its Inscription.\n\nOn a later turn any of your pawns can replay an Epitaph exactly as recorded, on any top, without spending a card, once per round. You hold one now: Red 2 straight ahead, then Blue 1 up and to the right. While you choose who replays it, hold a pawn to see where it would go before you pick it.',
  'tutorial.epitaph.task': 'Replay your Epitaph with a pawn that can follow its route.',
  'tutorial.epitaph.hint':
    'Press Replay Epitaph (or T). Hold your pawns on B1 and E2 to see where each would go, then pick the one on B1: it goes to B3, then captures on C4. Then press End turn.',
  'tutorial.epitaph.done': 'Replayed for {score} points, without spending a card.',
  'tutorial.epitaph.donePlus':
    'Replayed through a capture: {score} points, without spending a card.',
  'tutorial.epitaph.retry':
    'Play the Epitaph instead. It is free, while every card you spend is gone for good.',

  'tutorial.recover.title': 'Recover',
  'tutorial.recover.body':
    'Stuck with cards you can’t use? Recover throws one card from your hand away for good and draws the top card of your deck in its place. It takes your whole turn.\n\nThe top is Red 1, and nothing in your hand matches it.',
  'tutorial.recover.task': 'Recover a card you don’t want.',
  'tutorial.recover.hint': 'Press Recover (or R), then pick any card in your hand.',
  'tutorial.recover.done':
    'Card replaced. Recover fixes a hopeless hand, but the card is gone for the rest of the match.',
  'tutorial.recover.retry': 'Press Recover and pick a card.',

  'tutorial.pass.title': 'Passing and rounds',
  'tutorial.pass.body':
    'A round ends when both sides have passed, and the higher round score wins it. The first side to win two rounds wins the match; the total score only breaks ties.\n\nYour 26 cards must last the whole match: nothing is refilled between rounds, so every card you spend now is one fewer for the rounds to come.',
  'tutorial.pass.task':
    'You lead 34 to 21 and North has already passed. Pass to win the round without spending another card.',
  'tutorial.pass.hint': 'Press Pass, beside the pile, or press P.',
  'tutorial.pass.done': 'Round won, and your cards are saved for the next rounds.',
  'tutorial.pass.retry':
    'You were already winning and North can’t play again this round, so any card you spend now is simply lost for the next rounds. Pass instead.',

  'tutorial.finish.title': 'You know the rules',
  'tutorial.finish.intro': 'A few things good players keep in mind:',
  'tutorial.finish.tip1':
    'Win rounds, not points. Two round wins take the match, and a huge score in one round counts for nothing more.',
  'tutorial.finish.tip2':
    'Budget your cards. 26 cards must cover up to three rounds: about eight a round keeps you in every one, and losing a round cheaply can win you the match.',
  'tutorial.finish.tip3':
    'Chain for multipliers. Two or three links with a pattern score many times what single cards do.',
  'tutorial.finish.tip4':
    'Mind the top. The card you finish on is the one your opponent has to match.',
  'tutorial.finish.tip5':
    'Advance with care. Pawns on your home row are safe; pawns in the open can be captured for 5.',
  'tutorial.finish.tip6':
    'Captures have a price. Capturing an inscribed pawn hands its owner an Epitaph: a free move later.',
  'tutorial.finish.tip7':
    'Pass at the right moment: when you are ahead and the opponent can’t catch up, or when catching up would cost too many cards.',
  'tutorial.finish.play': 'Play your first duel',
  'tutorial.finish.playHint': 'Against the Easy computer, as South',
  'tutorial.finish.again': 'Start the tutorial again',
  'tutorial.finish.rules': 'Read the full rules',

  'error.illegal': 'That action is not allowed: {message}',
  'error.storage': 'Could not save: {message}',
  'error.generic': 'Something went wrong: {message}',
  'error.reload': 'Reload',

  // The Long Night: log lines (LEDGER.MD).
  'log.brassGained': '{side} +¤{n} ({reason})',
  'log.brassLost': '{side} −¤{n} ({reason})',
  'log.curioUsed': '{side} uses the {curio}',
  'log.statusAdded': '{pawn} is {status}',
  'log.omen': '{pawn} lands on the {omen} on {square}',
  'log.omenAppeared': 'A {omen} opens on {square}',
  'log.fellTrapdoor': '{pawn} falls through a Trapdoor on {square}',
  'log.fellSmoldering': '{pawn} burns out on {square}',
  'log.horror.storm': 'Lightning strikes {pawn} on {square}: knocked back and Pinned.',
  'log.horror.turned-table':
    '{side} turns the table: every pawn now stands on the opposite square.',
  'log.horror.switch': '{side} switches two pawns: {pawn} now stands on {square}.',
  'log.horror.lights-out': 'The lights go out. Something moved in the dark.',
  'log.horror.blind-eye': 'One eye is blinded: the {half} of the board is in the dark.',
  'log.horror.half.left': 'left half (files A–C)',
  'log.horror.half.right': 'right half (files D–F)',
  'log.horror.clean-slate': '{side} sweeps the board: {n} pawns are back where they started.',
  'log.moved.draft': 'A Draft pushes {pawn} to {square}',
  'log.moved.homing': '{pawn} comes home to {square}',
  'log.moved.returned': '{pawn} returns to {square}',
  'log.moved.lodestone': 'A Lodestone pulls {pawn} to {square}',
  'log.moved.swap': '{pawn} swaps to {square}',
  'log.moved.shove': '{pawn} is shoved to {square}',
  'log.moved.glass': '{pawn} steps out of the Looking Glass on {square}',
  'log.burned': '{side} burns {card} on the Brazier',
  'log.trick': '{side} plays {trick}{on}',
  'log.trickOn': ' on {pawn}',
  'log.item.stolen': '{to} takes the {item} from {side}',
  'log.item.returned': 'The {item} goes back to its owner',
  'log.item.melted': "{side}'s {item} melts away",
  'log.item.foreclosed': 'The Broker forecloses on the {item}',
  'log.cardStamped': "{side}'s new card gets a {stamp}",
  'log.cardShattered': "{side}'s Glass card shatters",

  // The Long Night: items, Omens, statuses and House Rules.
  'item.red-thread': 'Red Thread',
  'item.red-thread.text': 'Each Red link: +3 P.',
  'item.blue-bead': 'Blue Bead',
  'item.blue-bead.text': 'Each Blue link: +3 P.',
  'item.four-leaf': 'Four-Leaf',
  'item.four-leaf.text': 'Each Green link: +4 P.',
  'item.canary-feather': 'Canary Feather',
  'item.canary-feather.text': 'Each Yellow hop: +2 P.',
  'item.odd-fellow': 'Odd Fellow',
  'item.odd-fellow.text': '+3 M if every link is a 1 or a 3.',
  'item.even-keel': 'Even Keel',
  'item.even-keel.text': '+4 M if every link is a 2.',
  'item.snap': 'Snap',
  'item.snap.text': '+3 M if your first link has exactly the face on top of the pile.',
  'item.bloodline': 'Bloodline',
  'item.bloodline.text': '×2 M if every card you have played this round has the same color.',
  'item.ladder-walker': 'Ladder Walker',
  'item.ladder-walker.text': 'Ladder: +5 M.',
  'item.mirror-twin': 'Mirror Twin',
  'item.mirror-twin.text': 'Echo: +2 M.',
  'item.ribbon': 'Ribbon',
  'item.ribbon.text': 'Streak: +2 M.',
  'item.plumb-line': 'Plumb Line',
  'item.plumb-line.text': 'A chain with no pattern: +2 M.',
  'item.compass-rose': 'Compass Rose',
  'item.compass-rose.text':
    'Full Circle: +3 M, and ending next to the starting square counts as returning to it.',
  'item.stairwell': 'Stairwell',
  'item.stairwell.text': 'Discovers Stair (1, 2, 3 in mixed colors). Stair: +2 M.',
  'item.prism': 'Prism',
  'item.prism.text': 'Discovers Prism (three different colors). Prism: +2 M.',
  'item.palindrome': 'Palindrome',
  'item.palindrome.text': 'Discovers Mirror (first and last link share a face). Mirror: +2 M.',
  'item.double-knot': 'Double Knot',
  'item.double-knot.text': 'Your two best patterns both count.',
  'item.chain-letter': 'Chain Letter',
  'item.chain-letter.text': 'Your chains may have 4 links.',
  'item.horseshoe': 'Horseshoe',
  'item.horseshoe.text': 'A chain that captures: +2 M.',
  'item.black-cat': 'Black Cat',
  'item.black-cat.text': 'Each inscribed pawn you capture: +8 P.',
  'item.bottle-tree': 'Bottle Tree',
  'item.bottle-tree.text': 'Each capture: +1 M.',
  'item.crows-feather': "Crow's Feather",
  'item.crows-feather.text': 'Each capture: +¤1 (up to ¤4 a table).',
  'item.butchers-hook': "Butcher's Hook",
  'item.butchers-hook.text':
    '×1 M. Gains ×0.25 for good whenever a chain captures two or more pawns.',
  'item.skeleton-key': 'Skeleton Key',
  'item.skeleton-key.text': "Enemy pawns on their home row aren't immune to you.",
  'item.paper-crown': 'Paper Crown',
  'item.paper-crown.text': 'Crowning: +8 P more.',
  'item.penny-on-the-track': 'Penny on the Track',
  'item.penny-on-the-track.text': 'Each crowning: +¤1 (up to ¤3 a table).',
  'item.homecoming': 'Homecoming',
  'item.homecoming.text': '+4 M if a crowned pawn ends its chain on its own home row.',
  'item.black-mirror': 'Black Mirror',
  'item.black-mirror.text': '×2 M if the chain pawn was crowned before the chain began.',
  'item.tallow-candle': 'Tallow Candle',
  'item.tallow-candle.text':
    '×1 M. Gains ×0.2 for good with every crowning; melts away if you lose a round.',
  'item.rubbings': 'Rubbings',
  'item.rubbings.text': 'Each Epitaph you receive: +¤1.',
  'item.mourning-bell': 'Mourning Bell',
  'item.mourning-bell.text': '+1 M for each of your pawns captured this round.',
  'item.gravediggers-spade': "Gravedigger's Spade",
  'item.gravediggers-spade.text': 'Epitaph replays: ×2 M.',
  'item.widows-veil': "Widow's Veil",
  'item.widows-veil.text': '+1 Epitaph slot.',
  'item.keepsake-locket': 'Keepsake Locket',
  'item.keepsake-locket.text': 'Your Epitaphs carry over between tables.',
  'item.seance-table': 'Séance Table',
  'item.seance-table.text':
    'You may begin a chain with your Epitaph’s replay, then add cards up to the chain limit.',
  'item.blood-money': 'Blood Money',
  'item.blood-money.text': 'Pawning a pawn for a Wild: +¤2 (up to ¤6 a table).',
  'item.rag-and-bone': 'Rag-and-Bone',
  'item.rag-and-bone.text': 'Each Wild link: +6 P.',
  'item.debt-collector': 'Debt Collector',
  'item.debt-collector.text': '+1 M for every pawn you have pawned this run.',
  'item.pawn-ticket': 'Pawn Ticket',
  'item.pawn-ticket.text': 'Your first Wild each round needs no collateral.',
  'item.sacrificial-lamb': 'Sacrificial Lamb',
  'item.sacrificial-lamb.text': '×1 M, plus ×0.5 for each of your pawns off the board.',
  'item.coin-in-the-shoe': 'Coin in the Shoe',
  'item.coin-in-the-shoe.text': '+¤3 at the end of every table.',
  'item.misers-purse': "Miser's Purse",
  'item.misers-purse.text': 'Thrift pays double, and its limit doubles.',
  'item.magpies-hoard': "Magpie's Hoard",
  'item.magpies-hoard.text': '+1 M for every ¤5 you hold.',
  'item.early-bird': 'Early Bird',
  'item.early-bird.text': 'If you pass first and still win the round: +¤4.',
  'item.piggy-bank': 'Piggy Bank',
  'item.piggy-bank.text': 'Its sell value grows by ¤2 after every table.',
  'item.golden-tooth': 'Golden Tooth',
  'item.golden-tooth.text': 'Interest has no limit.',
  'item.first-light': 'First Light',
  'item.first-light.text': 'Your first chain each round: ×2 M.',
  'item.last-orders': 'Last Orders',
  'item.last-orders.text': 'While your opponent has passed: +3 M.',
  'item.spilled-salt': 'Spilled Salt',
  'item.spilled-salt.text': '+1 M for every 3 cards on the pile.',
  'item.seventh-son': 'Seventh Son',
  'item.seventh-son.text': 'Every 7th chain you make in this run: ×3 M.',
  'item.grandfather-clock': 'Grandfather Clock',
  'item.grandfather-clock.text': '+1 M for every turn you have taken this round.',
  'item.thirteenth-step': 'Thirteenth Step',
  'item.thirteenth-step.text': 'One-link chains: +4 M.',
  'item.long-shadow': 'Long Shadow',
  'item.long-shadow.text': 'Chains of three or more links: +2 M.',
  'item.salt-line': 'Salt Line',
  'item.salt-line.text': 'A chain that ends on your half of the board: +6 P.',
  'item.cornerstone': 'Cornerstone',
  'item.cornerstone.text': 'A chain that ends on a corner: +4 M; on another edge square: +1 M.',
  'item.crossroads': 'Crossroads',
  'item.crossroads.text': 'Each landing on one of the four centre squares: +4 P.',
  'item.phalanx': 'Phalanx',
  'item.phalanx.text': '+1 M for each friendly pawn next to the chain pawn when the chain ends.',
  'item.tallow-stub': 'Tallow Stub',
  'item.tallow-stub.text': '+1 M for each of your pawns still on its home row.',
  'item.long-way-home': 'Long Way Home',
  'item.long-way-home.text': '+2 P for each square the chain pawn travelled.',
  'item.lucky-coin': 'Lucky Coin',
  'item.lucky-coin.text': '1 in 3: +¤2 when a chain scores.',
  'item.loaded-die': 'Loaded Die',
  'item.loaded-die.text': '1 in 3: ×2 M.',
  'item.wishbone': 'Wishbone',
  'item.wishbone.text': '1 in 2: a card drawn by Recover gets a random Stamp for the run.',
  'item.rabbits-foot': "Rabbit's Foot",
  'item.rabbits-foot.text': 'Doubles every chance (1 in 3 becomes 2 in 3).',
  'item.mimic-jar': 'Mimic Jar',
  'item.mimic-jar.text': 'Copies the Superstition to its right.',
  'item.reliquary': 'Reliquary',
  'item.reliquary.text': 'Copies the leftmost Superstition.',
  'item.empty-chair': 'Empty Chair',
  'item.empty-chair.text': '×1 M for each empty Mantel slot, this one included.',
  'item.the-unredeemed': 'The Unredeemed',
  'item.the-unredeemed.text':
    'Pawning an inscribed pawn sends you its Epitaph. Your Epitaph replays gain ×1.5 M for each Epitaph you have played at this table.',
  'item.the-last-pawn': 'The Last Pawn',
  'item.the-last-pawn.text': '×4 M while you have exactly one pawn on the board.',
  'item.saints-finger': "Saint's Finger",
  'item.saints-finger.text': 'Your cards match any top.',
  'item.pawn-kings-crown': "The Pawn King's Crown",
  'item.pawn-kings-crown.text': 'Gains ×0.5 M for good every time one of your pawns is crowned.',
  'item.brokers-monocle': "The Broker's Monocle",
  'item.brokers-monocle.text': "You see your opponent's hand.",
  'item.spurs': 'Spurs',
  'item.spurs.text': 'Hasted at the start of every round.',
  'item.hollow-base': 'Hollow Base',
  'item.hollow-base.text': 'Its slides pass through friendly pawns.',
  'item.tusk': 'Tusk',
  'item.tusk.text': '+4 P for each capture it makes.',
  'item.pockets': 'Pockets',
  'item.pockets.text': 'When it is crowned, draw a card.',
  'item.tin-heart': 'Tin Heart',
  'item.tin-heart.text': 'When it is captured, your next chain is Inspired (+2 M).',
  'item.gilt-base': 'Gilt Base',
  'item.gilt-base.text': '+¤1 whenever it captures or is crowned.',
  'item.scribes-mark': "Scribe's Mark",
  'item.scribes-mark.text': 'Epitaphs made from it get +2 M when replayed.',
  'item.homing': 'Homing',
  'item.homing.text':
    'Once per round, when captured, it returns to its starting square at the start of your next turn.',
  'item.iron-shell': 'Iron Shell',
  'item.iron-shell.text': "It can't be jumped.",
  'item.lantern': 'Lantern',
  'item.lantern.text': 'When its chain ends, friendly pawns next to it become Warded.',
  'item.glutton': 'Glutton',
  'item.glutton.text': '+1 P for good, on its own chains, for every capture it makes.',
  'item.dowser': 'Dowser',
  'item.dowser.text': '+3 P for each Omen it lands on.',
  'item.thorns': 'Thorns',
  'item.thorns.text': 'The pawn that captures it becomes Pinned.',
  'item.many-lives': 'Many Lives',
  'item.many-lives.text':
    "Once per round, it can pay a Wild's collateral without leaving the board.",
  'item.bell-ringer': 'Bell Ringer',
  'item.bell-ringer.text':
    'When it is crowned, one of your fallen pawns returns to its starting square.',
  'item.crowned-head': 'Crowned Head',
  'item.crowned-head.text': "Starts every round crowned (its crowning can't score).",
  'item.knights-gait': "Knight's Gait",
  'item.knights-gait.text':
    "A Green card moves it up to N knight jumps, where N is the card's number.",
  'item.comet': 'Comet',
  'item.comet.text':
    'Its Red and Blue slides go through enemy pawns and capture each one they pass.',
  'item.twin-soul': 'Twin Soul',
  'item.twin-soul.text':
    'When its chain is inscribed, every friendly pawn without an inscription gets the same one.',
  'item.bellwether': 'Bellwether',
  'item.bellwether.text': 'Its chains count one extra link for M.',
  'item.kite': 'Kite',
  'item.kite.text': 'Its Yellow hops may also go straight.',
  'item.brass-stamp': 'Brass Stamp',
  'item.brass-stamp.text': '+5 P.',
  'item.wax-seal': 'Wax Seal',
  'item.wax-seal.text': '+2 M.',
  'item.coin-stamp': 'Coin Stamp',
  'item.coin-stamp.text': '+¤1.',
  'item.double-stamp': 'Double Stamp',
  'item.double-stamp.text': "This link's landing, capture and crowning points count twice.",
  'item.return-stamp': 'Return Stamp',
  'item.return-stamp.text': 'When the round ends, the card goes back to the bottom of your deck.',
  'item.glass-stamp': 'Glass Stamp',
  'item.glass-stamp.text': '×2 M; 1 in 4 it shatters (gone for the run).',
  'item.lucky-stamp': 'Lucky Stamp',
  'item.lucky-stamp.text': '1 in 5: +20 P. 1 in 15: +¤10.',
  'item.chalk-stamp': 'Chalk Stamp',
  'item.chalk-stamp.text': 'Matches any top.',
  'item.ward-stamp': 'Ward Stamp',
  'item.ward-stamp.text': 'The chain pawn becomes Warded.',
  'item.tally-stamp': 'Tally Stamp',
  'item.tally-stamp.text': '+1 P for good every time it is played.',
  'item.foil': 'Foil',
  'item.foil.text': '+10 P, on a card or on a Superstition.',
  'item.gilt': 'Gilt',
  'item.gilt.text': '+3 M on a card, +4 M on a Superstition.',
  'item.pearl': 'Pearl',
  'item.pearl.text': '×1.5 M.',
  'item.ghost': 'Ghost',
  'item.ghost.text':
    'On a card: +1 hand limit at every table. On a Superstition: takes no Mantel slot.',
  'item.chalk': 'Chalk',
  'item.chalk.text': 'Set the top of the pile to any face.',
  'item.hourglass': 'Hourglass',
  'item.hourglass.text': 'Your opponent skips their next turn.',
  'item.pliers': 'Pliers',
  'item.pliers.text': 'Pin an enemy pawn.',
  'item.scissors': 'Scissors',
  'item.scissors.text': "Cut an enemy pawn's inscription: it becomes Hollow.",
  'item.bell-jar': 'Bell Jar',
  'item.bell-jar.text': 'Ward one of your pawns until the round ends.',
  'item.smelling-salts': 'Smelling Salts',
  'item.smelling-salts.text': 'One of your fallen pawns returns to its starting square.',
  'item.pocket-watch': 'Pocket Watch',
  'item.pocket-watch.text': 'Draw up to 2 cards.',
  'item.black-candle': 'Black Candle',
  'item.black-candle.text': 'Your next chain: ×2 M.',
  'item.lodestone': 'Lodestone',
  'item.lodestone.text': 'Pull an enemy pawn one square toward one of your pawns.',
  'item.rag-doll': 'Rag Doll',
  'item.rag-doll.text': "Pays a Wild's collateral instead of a pawn.",
  'item.planchette': 'Planchette',
  'item.planchette.text': 'Replay any Epitaph on the pile this round with one of your pawns.',
  'item.cracked-mirror': 'Cracked Mirror',
  'item.cracked-mirror.text': "Copy your opponent's held Epitaph into your slot.",
  'item.spyglass': 'Spyglass',
  'item.spyglass.text': 'Swap a card in your hand for the top card of your deck.',
  'item.matchbook': 'Matchbook',
  'item.matchbook.text': 'An enemy pawn becomes Smoldering.',
  'item.omen.candle': 'Candle',
  'item.omen.candle.text': 'Landing: +1 M and Kindled. Burns out after 3 landings.',
  'item.omen.gilt-tile': 'Gilt Tile',
  'item.omen.gilt-tile.text': 'Landing: +¤1, once per round.',
  'item.omen.salt-circle': 'Salt Circle',
  'item.omen.salt-circle.text': 'A pawn standing on it is Warded.',
  'item.omen.pillar': 'Pillar',
  'item.omen.pillar.text': 'A stone block: blocks slides, can be hopped, never captured.',
  'item.omen.well': 'Well',
  'item.omen.well.text': 'Landing: draw a card, once per round.',
  'item.omen.ink-pool': 'Ink Pool',
  'item.omen.ink-pool.text': 'Landing: +3 P; the chain replaces the inscription.',
  'item.omen.chalk-x': 'Chalk X',
  'item.omen.chalk-x.text': 'Landing with a 3: +10 P.',
  'item.omen.thorns': 'Thorns',
  'item.omen.thorns.text': 'Sliding through it: Pinned.',
  'item.omen.altar': 'Altar',
  'item.omen.altar.text': 'Crowning on it: +2 M.',
  'item.omen.trapdoor': 'Trapdoor',
  'item.omen.trapdoor.text': 'A move that ends here pawns the pawn.',
  'item.omen.lost-ring': 'Lost Ring',
  'item.omen.lost-ring.text': 'Landing: +¤3, then it is gone.',
  'item.omen.draft': 'Draft',
  'item.omen.draft.text': 'A move that ends here is pushed one square.',
  'item.omen.mimic': 'Mimic',
  'item.omen.mimic.text': 'Becomes a random Omen when landed on.',
  'item.omen.looking-glass': 'Looking Glass',
  'item.omen.looking-glass.text':
    'A link that ends here steps out of the Looking Glass opposite, if it is free, and the chain goes on from there.',
  'item.omen.brazier': 'Brazier',
  'item.omen.brazier.text':
    'Landing here: your next link may use any card, whatever the top. That card burns: it leaves the match, and the top stays.',
  'item.omen.unknown': 'Dark square',
  'item.omen.unknown.text': 'A Shrouded Omen: nobody knows what it is until something lands on it.',
  'item.status.warded': 'Warded',
  'item.status.warded.text': "Can't be captured.",
  'item.status.hasted': 'Hasted',
  'item.status.hasted.text': 'Its next Red or Blue link may go one square shorter or longer.',
  'item.status.kindled': 'Kindled',
  'item.status.kindled.text': '+2 P per landing in its chains, per stack.',
  'item.status.phased': 'Phased',
  'item.status.phased.text': 'Its slides pass through pieces.',
  'item.status.gilded': 'Gilded',
  'item.status.gilded.text': '+¤1 whenever it captures or is crowned.',
  'item.status.pinned': 'Pinned',
  'item.status.pinned.text': "Can't move or pay a Wild's collateral.",
  'item.status.exposed': 'Exposed',
  'item.status.exposed.text': 'Not immune on its home row; capturing it gives +5 P.',
  'item.status.cursed': 'Cursed',
  'item.status.cursed.text': 'Its chains score ×0.5 M.',
  'item.status.smoldering': 'Smoldering',
  'item.status.smoldering.text': "If it hasn't moved when the fuse burns out, it is pawned.",
  'item.status.hollow': 'Hollow',
  'item.status.hollow.text': 'Has no inscription and leaves no Epitaph.',
  'item.status.heavy': 'Heavy',
  'item.status.heavy.text': 'Red and Blue cards move it exactly one square.',
  'item.status.inspired': 'Inspired',
  'item.status.inspired.text': 'Your next chain: +2 M.',
  'item.status.black-candle': 'Black Candle',
  'item.status.black-candle.text': 'Your next chain: ×2 M.',
  'item.status.free-wild': 'Rag Doll',
  'item.status.free-wild.text': 'Your next Wild needs no collateral.',
  'item.rule.short-wick': 'Short Wick',
  'item.rule.short-wick.text': 'You get {n} turns a round; after the last, you pass.',
  'item.rule.drip': 'Drip',
  'item.rule.drip.text': 'Cards left in your hand at the end of a round melt.',
  'item.rule.snuffed': 'Snuffed',
  'item.rule.snuffed.text': "Your Superstitions don't trigger on one-link chains.",
  'item.rule.sticky-fingers': 'Sticky Fingers',
  'item.rule.sticky-fingers.text':
    'Each round she takes your rightmost Superstition for that round.',
  'item.rule.gleam': 'Gleam',
  'item.rule.gleam.text': 'Your Stamps and Finishes do nothing at her table.',
  'item.rule.pickpocket': 'Pickpocket',
  'item.rule.pickpocket.text': 'Each capture she makes takes ¤{n} from you.',
  'item.rule.mourning-dress': 'Mourning Dress',
  'item.rule.mourning-dress.text': 'The Epitaphs of your captured pawns go to her.',
  'item.rule.last-rites': 'Last Rites',
  'item.rule.last-rites.text':
    "Your captured pawns don't come back at round setup unless you won the round.",
  'item.rule.veiled': 'Veiled',
  'item.rule.veiled.text': "Her pawns' inscriptions are hidden from you.",
  'item.rule.collateral-call': 'Collateral Call',
  'item.rule.collateral-call.text':
    "Each round he takes the Superstition you've triggered most; win a round to get it back.",
  'item.rule.foreclosure': 'Foreclosure',
  'item.rule.foreclosure.text': 'Each round you lose, he keeps one of your Superstitions for good.',
  'item.rule.salted-earth': 'Salted Earth',
  'item.rule.salted-earth.text': 'Landings score no P.',
  'item.rule.tarnish': 'Tarnish',
  'item.rule.tarnish.text': 'Your {color} cards score no P.',
  'item.rule.heavy-chains': 'Heavy Chains',
  'item.rule.heavy-chains.text': 'Chains have at most 2 links.',
  'item.rule.no-repeats': 'No Repeats',
  'item.rule.no-repeats.text': 'Each pattern scores at most once per round.',
  'item.rule.single-file': 'Single File',
  'item.rule.single-file.text':
    "After your first chain, every chain must begin with that chain's color.",
  'item.rule.toll': 'Toll',
  'item.rule.toll.text': '−¤{n} for every card you play.',
  'item.rule.iron-ranks': 'Iron Ranks',
  'item.rule.iron-ranks.text': 'Two rows on each side count as home rows.',
  'item.rule.short-hand': 'Short Hand',
  'item.rule.short-hand.text': 'Hand limit −1.',
  'item.rule.rust': 'Rust',
  'item.rule.rust.text': "Faces you've already played at this table score no P.",
  'item.rule.heavy-toll': 'Heavy Toll',
  'item.rule.heavy-toll.text': "Reserves can't pay for Wilds.",
  'item.rule.clock': 'Clock',
  'item.rule.clock.text': 'Each side gets {n} turns a round.',
  'item.rule.warded-start': 'Warded start',
  'item.rule.warded-start.text': 'Pawns start the round Warded.',
  'item.rule.storm': 'The Storm',
  'item.rule.storm.text':
    'Every second turn of his, lightning strikes your pawn that has come furthest: it is knocked back a square and Pinned until your next turn is over.',
  'item.rule.turned-table': 'The Turned Table',
  'item.rule.turned-table.text':
    'On his third turn of each round his hands turn the board half round: every pawn ends on the opposite square, yours where his stood.',
  'item.rule.switch': 'The Switch',
  'item.rule.switch.text':
    'On his second and fifth turns of each round he trades your pawn that has come furthest for his hindmost one.',
  'item.rule.lights-out': 'Lights Out',
  'item.rule.lights-out.text':
    'Once a round the lamp goes out. In the dark his furthest pawn steals a step forward, and yours is nudged a square aside.',
  'item.rule.blind-eye': 'The Blind Eye',
  'item.rule.blind-eye.text':
    'He blinds one of your eyes: half the board is in the dark, and nothing there can move or be moved onto. After six turns the dark crosses to the other half.',
  'item.rule.clean-slate': 'Clean Slate',
  'item.rule.clean-slate.text':
    'Once a round, when one of your pawns stands a step from crowning, his hands sweep the board: every pawn goes back to where it started.',
  'item.reason.toll': 'Toll',
  'item.reason.pickpocket': 'Pickpocket',
  'item.reason.gilded': 'Gilded',
  'item.reason.gilt-tile': 'Gilt Tile',
  'item.reason.lost-ring': 'Lost Ring',
  'item.reason.tithe': 'Tithe',
  'item.trick.reverse': 'Reverse',
  'item.trick.reverse.text':
    "On your opponent's next turn, their uncrowned pawns may move only backward (or sideways).",
  'item.trick.gag': 'Gag',
  'item.trick.gag.text': "Your opponent's next chain can have only 1 link.",
  'item.trick.swap': 'Swap',
  'item.trick.swap.text': 'The chain pawn swaps squares with another of your pawns.',
  'item.trick.shove': 'Shove',
  'item.trick.shove.text':
    'Push an enemy pawn next to the chain pawn one square straight away from it, onto an empty square.',
  'item.trick.chalk-line': 'Chalk Line',
  'item.trick.chalk-line.text': 'Name the face this card counts as. No collateral, no move.',
  'item.trick.draw-two': 'Draw Two',
  'item.trick.draw-two.text': 'Draw 2 cards (up to your hand limit).',
  'item.trick.hex': 'Hex',
  'item.trick.hex.text': 'An enemy pawn becomes Pinned.',
  'item.trick.requiem': 'Requiem',
  'item.trick.requiem.text': 'One of your fallen pawns returns to its starting square, if empty.',
  'item.trick.tithe': 'Tithe',
  'item.trick.tithe.text': '+¤2.',
  'trick.play': 'Play it',
  'trick.chainPawn': 'The chain pawn (it stays where it is)',
  'item.trick': 'Trick',
  'item.trick.text':
    'A Trick matches the pile and counts as a link (for the multiplier and patterns), but it moves nothing: it does its effect instead.',
  'item.status.reversed': 'Reversed',
  'item.status.reversed.text': 'This turn your uncrowned pawns may move only backward or sideways.',
  'item.status.gagged': 'Gagged',
  'item.status.gagged.text': 'Your next chain can have only 1 link.',

  // The Long Night: the Counter's goods, Pledges, Terms, Patrons and Detours.
  'item.appraisal.ladder': "Jeweler's Loupe",
  'item.appraisal.ladder.text': 'Ladder: +6 P and +2 M per level.',
  'item.appraisal.echo': 'Tuning Fork',
  'item.appraisal.echo.text': 'Echo: +4 P and +1 M per level.',
  'item.appraisal.streak': 'Spool of Thread',
  'item.appraisal.streak.text': 'Streak: +4 P and +1 M per level.',
  'item.appraisal.stair': 'Stair Rod',
  'item.appraisal.stair.text': 'Stair: +5 P and +1 M per level. Discovers Stair.',
  'item.appraisal.prism': 'Prism Glass',
  'item.appraisal.prism.text': 'Prism: +5 P and +1 M per level. Discovers Prism.',
  'item.appraisal.mirror': 'Hand Mirror',
  'item.appraisal.mirror.text': 'Mirror: +6 P and +2 M per level. Discovers Mirror.',
  'item.appraisal.fullCircle': 'Brass Compass',
  'item.appraisal.fullCircle.text':
    'Full Circle (ending where the chain began, by a route that is not one straight line): +8 P and +2 M per level.',
  'item.appraisal.none': 'Plumb Bob',
  'item.appraisal.none.text': 'Chains with no pattern: +3 P and +1 M per level.',
  'item.appraisal.capture': "Butcher's Scale",
  'item.appraisal.capture.text': '+3 P per capture, per level.',
  'item.appraisal.crown': 'Tiara',
  'item.appraisal.crown.text': '+6 P per crowning, per level.',
  'item.appraisal.landing': "Surveyor's Chain",
  'item.appraisal.landing.text': '+1 P per landing, per level.',
  'item.appraisal.epitaph': 'Grave Rubbing',
  'item.appraisal.epitaph.text': 'Epitaph replays: +5 P and +1 M per level.',
  'item.whittling-knife': 'Whittling Knife',
  'item.whittling-knife.text': 'Remove up to 2 cards from your run deck.',
  'item.ink-pot': 'Ink Pot',
  'item.ink-pot.text': 'Up to 2 cards take the color of a third.',
  'item.file': 'File',
  'item.file.text': "Raise or lower a card's number by 1.",
  'item.rubber-stamp': 'Rubber Stamp',
  'item.rubber-stamp.text': 'Up to 2 cards get a random Stamp.',
  'item.tracing-paper': 'Tracing Paper',
  'item.tracing-paper.text': 'Copy a card, with its Stamp.',
  'item.coin-purse': 'Coin Purse',
  'item.coin-purse.text': 'Double your brass (up to +¤20).',
  'item.appraisers-note': "Appraiser's Note",
  'item.appraisers-note.text': '+1 level to the pattern you scored most at the last table.',
  'item.fixture.glass-case': 'Glass Case',
  'item.fixture.glass-case.text': '+1 tray slot.',
  'item.fixture.display-wall': 'Display Wall',
  'item.fixture.display-wall.text': '+1 more tray slot.',
  'item.fixture.brass-scale': 'Brass Scale',
  'item.fixture.brass-scale.text': 'Everything 25% cheaper.',
  'item.fixture.gold-scale': 'Gold Scale',
  'item.fixture.gold-scale.text': 'Everything 50% cheaper.',
  'item.fixture.strongbox': 'Strongbox',
  'item.fixture.strongbox.text': 'Interest up to ¤10.',
  'item.fixture.vault-door': 'Vault Door',
  'item.fixture.vault-door.text': 'Interest up to ¤20.',
  'item.fixture.shop-bell': 'Shop Bell',
  'item.fixture.shop-bell.text': 'Ringing costs ¤2 less.',
  'item.fixture.silver-bell': 'Silver Bell',
  'item.fixture.silver-bell.text': 'Ringing costs ¤2 less again.',
  'item.fixture.coat-hook': 'Coat Hook',
  'item.fixture.coat-hook.text': '+1 Curio slot.',
  'item.fixture.coat-lining': 'Coat Lining',
  'item.fixture.coat-lining.text': 'Curios cost ¤1.',
  'item.fixture.wall-shelf': 'Wall Shelf',
  'item.fixture.wall-shelf.text': 'Superstitions are twice as likely to be Uncommon or better.',
  'item.fixture.altar-shelf': 'Altar Shelf',
  'item.fixture.altar-shelf.text': '+1 Mantel slot.',
  'item.fixture.long-fingers': 'Long Fingers',
  'item.fixture.long-fingers.text': 'Hand limit +1.',
  'item.fixture.sleight-of-hand': 'Sleight of Hand',
  'item.fixture.sleight-of-hand.text': 'Hand limit +1 more.',
  'item.fixture.deep-pockets': 'Deep Pockets',
  'item.fixture.deep-pockets.text': 'Cut +2 at one-round tables.',
  'item.fixture.bottomless-pockets': 'Bottomless Pockets',
  'item.fixture.bottomless-pockets.text': 'Cut +2 more.',
  'item.fixture.grave-plot': 'Grave Plot',
  'item.fixture.grave-plot.text': '+1 Epitaph slot.',
  'item.fixture.family-crypt': 'Family Crypt',
  'item.fixture.family-crypt.text': '+1 Epitaph play per round.',
  'item.fixture.carvers-bench': "Carver's Bench",
  'item.fixture.carvers-bench.text': 'Carvings appear in the tray.',
  'item.fixture.masters-bench': "Master's Bench",
  'item.fixture.masters-bench.text': 'Pawns can hold 2 Carvings.',
  'item.fixture.stamp-rack': 'Stamp Rack',
  'item.fixture.stamp-rack.text': 'Tray cards often have a Stamp.',
  'item.fixture.engraving-press': 'Engraving Press',
  'item.fixture.engraving-press.text': 'Card Lots show 2 more cards.',
  'item.fixture.appraisers-desk': "Appraiser's Desk",
  'item.fixture.appraisers-desk.text': 'Appraisal Lots show 1 more.',
  'item.fixture.assay-office': 'Assay Office',
  'item.fixture.assay-office.text': 'Appraisals give 2 levels.',
  'item.fixture.ledger': 'Ledger',
  'item.fixture.ledger.text': 'You may go into debt down to −¤10.',
  'item.fixture.black-ledger': 'Black Ledger',
  'item.fixture.black-ledger.text': 'Debt down to −¤25.',
  'item.fixture.oil-lamp': 'Oil Lamp',
  'item.fixture.oil-lamp.text': 'Shrouded Omens are revealed.',
  'item.fixture.second-sight': 'Second Sight',
  'item.fixture.second-sight.text':
    'You see further: High Tables show their House Rules a Night ahead.',
  'item.fixture.hiring-board': 'Hiring Board',
  'item.fixture.hiring-board.text': 'Hiring costs ¤2 less; hired pawns come with a Carving.',
  'item.fixture.guild-hall': 'Guild Hall',
  'item.fixture.guild-hall.text': '+1 recruit per High Table won.',
  'item.bargain.soul-ticket': 'Soul Ticket',
  'item.bargain.soul-ticket.text': '2 pawns: create a Legendary Superstition.',
  'item.bargain.effigy': 'Effigy',
  'item.bargain.effigy.text': '1 pawn: copy one of your Superstitions and destroy all the others.',
  'item.bargain.graft': 'The Graft',
  'item.bargain.graft.text':
    'Move a Carving from one pawn to another; the donor goes to the Broker.',
  'item.bargain.fusion': 'The Fusion',
  'item.bargain.fusion.text':
    'Fuse two pawns: one keeps both Carvings, the other goes to the Broker.',
  'item.bargain.mortgage': 'Mortgage',
  'item.bargain.mortgage.text': '+¤25, and two IOU curse cards in your deck.',
  'item.bargain.blood-ink': 'Blood Ink',
  'item.bargain.blood-ink.text': '1 pawn: a random Superstition gets Pearl.',
  'item.bargain.burnt-ledger': 'Burnt Ledger',
  'item.bargain.burnt-ledger.text': 'All your brass: +1 level to every Appraisal.',
  'item.bargain.changeling': 'Changeling',
  'item.bargain.changeling.text': '¤5: four random cards take the color of a card you choose.',
  'item.bargain.hollow-crown': 'Hollow Crown',
  'item.bargain.hollow-crown.text': '1 pawn: your pawns start every High Table crowned.',
  'item.bargain.black-wild': 'Black Wild',
  'item.bargain.black-wild.text':
    'Two Wilds join your deck; the next table’s head start is half again as large.',
  'item.bargain.exhumation': 'The Exhumation',
  'item.bargain.exhumation.text': "1 pawn: a lost pawn's best route becomes your Epitaph.",
  'item.bargain.brokers-hand': "The Broker's Hand",
  'item.bargain.brokers-hand.text':
    '+1 Mantel slot; every head start is 25% higher for the rest of the run.',
  'item.favor.sealed-lot': 'Sealed Lot',
  'item.favor.sealed-lot.text': 'A free Grand Lot at the next Counter.',
  'item.favor.coupon': 'Coupon',
  'item.favor.coupon.text': 'The tray and Lots at the next Counter are free.',
  'item.favor.investment': 'Investment',
  'item.favor.investment.text': '+¤20 after the next High Table.',
  'item.favor.rare-tip': 'Rare Tip',
  'item.favor.rare-tip.text': 'The next Counter has a free Rare Superstition.',
  'item.favor.pearl-tip': 'Pearl Tip',
  'item.favor.pearl-tip.text': 'The next Superstition you buy gets Pearl.',
  'item.favor.double': 'Double',
  'item.favor.double.text': 'The next Favor you take, you take twice.',
  'item.favor.handful': 'Handful',
  'item.favor.handful.text': 'Cut +3 at the next one-round table.',
  'item.favor.starlight': 'Starlight',
  'item.favor.starlight.text': '+3 levels to a random Appraisal.',
  'item.favor.speed': 'Speed',
  'item.favor.speed.text': '+¤5 for every table skipped this run.',
  'item.favor.bounty': 'Bounty',
  'item.favor.bounty.text': '+¤2 for each capture at the next table.',
  'item.favor.ward': 'Ward',
  'item.favor.ward.text': 'Your pawns start the next table Warded.',
  'item.favor.toll-waiver': 'Toll Waiver',
  'item.favor.toll-waiver.text': "The next High Table's head start is halved.",
  'item.favor.mourning': 'Mourning',
  'item.favor.mourning.text': 'A ready-made Epitaph for the next table.',
  'item.favor.carvers': "Carver's Favor",
  'item.favor.carvers.text': 'A free Carving (1 of 3) at the next Counter.',
  'item.lot.card': 'Card Lot',
  'item.lot.card.text': 'Deck cards, often with a Stamp or Finish.',
  'item.lot.hearsay': 'Hearsay Lot',
  'item.lot.hearsay.text': 'Superstitions.',
  'item.lot.appraisal': 'Appraisal Lot',
  'item.lot.appraisal.text': 'Appraisals.',
  'item.lot.curio': 'Lost and Found Lot',
  'item.lot.curio.text': 'Curios.',
  'item.lot.carver': "Carver's Lot",
  'item.lot.carver.text': 'Carvings for your pawns.',
  'item.lot.mourning': 'Mourning Lot',
  'item.lot.mourning.text': 'Ready-made Epitaph routes.',
  'item.lot.omen': 'Omen Lot',
  'item.lot.omen.text': 'An Omen you place on the next board.',
  'item.lot.estate': 'Estate Lot',
  'item.lot.estate.text': 'Bargains, with their costs built in.',
  'item.pledge.pocket-watch': 'Pocket Watch',
  'item.pledge.pocket-watch.text': 'The base deck; you start with ¤8.',
  'item.pledge.pocket-watch.unlock': 'Available from the start.',
  'item.pledge.wedding-ring': 'Wedding Ring',
  'item.pledge.wedding-ring.text': "Widow's Veil; no Wilds in the deck.",
  'item.pledge.wedding-ring.unlock': 'Replay 5 Epitaphs.',
  'item.pledge.fiddle': 'Fiddle',
  'item.pledge.fiddle.text': 'Only Red and Blue cards, four of each face, and two Wilds; Ribbon.',
  'item.pledge.fiddle.unlock': 'Score 10 Streaks.',
  'item.pledge.locket': 'Locket',
  'item.pledge.locket.text': 'Keepsake Locket, and your latest Heirloom.',
  'item.pledge.locket.unlock': 'Lose a pawn to the Broker.',
  'item.pledge.dice': 'Dice',
  'item.pledge.dice.text': 'Loaded Die and Lucky Coin; one fewer Mantel slot.',
  'item.pledge.dice.unlock': 'Win 10 chances.',
  'item.pledge.ivory-set': 'Ivory Set',
  'item.pledge.ivory-set.text': 'Every roster pawn carved with Spurs; no Yellow cards.',
  'item.pledge.ivory-set.unlock': 'Crown 6 pawns at one table.',
  'item.pledge.glass-eye': 'Glass Eye',
  'item.pledge.glass-eye.text': 'The Oil Lamp; twice as many Omens.',
  'item.pledge.glass-eye.unlock': 'Land on 20 Omens.',
  'item.pledge.iou': 'IOU',
  'item.pledge.iou.text': 'Start at −¤10 with the Ledger; one more Mantel slot.',
  'item.pledge.iou.unlock': 'Be in debt at a Counter.',
  'item.pledge.bare-box': 'Bare Box',
  'item.pledge.bare-box.text': 'No 3s in the deck; cut +2.',
  'item.pledge.bare-box.unlock': 'Win a run.',
  'item.pledge.scale': 'Scale',
  'item.pledge.scale.text': 'Head starts are doubled.',
  'item.pledge.scale.unlock': 'Win a run at Term IV.',
  'item.pledge.shuffled-box': 'Shuffled Box',
  'item.pledge.shuffled-box.text': '26 random cards.',
  'item.pledge.shuffled-box.unlock': 'Win with three different Pledges.',
  'item.pledge.ash': 'Ash',
  'item.pledge.ash.text': 'A random Rare Superstition; every Superstition rots after 5 tables.',
  'item.pledge.ash.unlock': 'Win a run at Term VI.',
  'night.term.1': 'The standard run.',
  'night.term.2': 'Patrons bring their quirks.',
  'night.term.3': 'Head starts ×1.3.',
  'night.term.4': 'Some Superstitions are On Loan: ¤1 to buy, ¤1 a table, worth nothing when sold.',
  'night.term.5': 'Big Tables pay ¤1 less; ringing costs ¤1 more.',
  'night.term.6': 'High Tables have a second House Rule.',
  'night.term.7':
    'Some Superstitions you buy are Rotting (they stop working after 5 tables) or Bolted (they can’t be sold).',
  'night.term.8': 'Lose a table and every fielded named pawn goes to the Broker.',
  'item.patron.sailor': 'The Sailor',
  'item.patron.sailor.text': 'Two Draft Omens on the board.',
  'item.patron.bride': 'The Bride',
  'item.patron.bride.text': "Holds Widow's Veil.",
  'item.patron.clockmaker': 'The Clockmaker',
  'item.patron.clockmaker.text': 'Each side gets 6 turns a round.',
  'item.patron.gambler': 'The Gambler',
  'item.patron.gambler.text': 'Holds Loaded Die.',
  'item.patron.priest': 'The Priest',
  'item.patron.priest.text': 'His pawns start Warded.',
  'item.patron.thief': 'The Thief',
  'item.patron.thief.text': 'Each capture he makes takes ¤1 from you.',
  'item.patron.twins': 'The Twins',
  'item.patron.twins.text': 'Hold two Mirror Twins.',
  'item.patron.soldier': 'The Soldier',
  'item.patron.soldier.text': 'Holds Phalanx.',
  'item.detour.workbench': 'The Workbench',
  'item.detour.workbench.text': 'Carve a pawn: choose 1 of 3 Carvings, free.',
  'item.detour.furnace': 'The Furnace',
  'item.detour.furnace.text': 'Melt up to 3 cards free, or give one card a Stamp of your choice.',
  'item.detour.seance': 'The Séance',
  'item.detour.seance.text': 'Pay a pawn and choose 1 of 3 Bargains.',
  'item.detour.puzzle-box': 'The Puzzle Box',
  'item.detour.puzzle-box.text':
    'Find a chain that scores at least the target this turn. Solve it for a Rare Superstition.',
  'item.detour.lost-and-found': 'Lost and Found',
  'item.detour.lost-and-found.text': 'Take 1 of 3 Curios free, and ¤3.',
  'item.detour.twins': 'The Twins',
  'item.detour.twins.text':
    'Fuse two cards with the same face into one, with a Double Stamp and Foil.',
  'item.detour.fence': 'The Fence',
  'item.detour.fence.text':
    'Rare Superstitions at half price, but stolen: the Magpie will want them back.',
  'item.detour.auction': 'The Auction',
  'item.detour.auction.text': 'Bid in a sealed auction against the regulars for one rare prize.',
  'item.detour.confessional': 'The Confessional',
  'item.detour.confessional.text': 'Every IOU is forgiven; ¤8 lifts the rot from Rotting items.',
  'item.detour.cartographer': 'The Cartographer',
  'item.detour.cartographer.text':
    "Choose which Omens will mark the squares at the next Night's tables: 3 of these 6.",
  'item.broker.collateral-call': 'Collateral Call',
  'item.broker.collateral-call.text':
    "Each round he takes the Superstition you've triggered most; win a round to get it back.",
  'item.broker.final-appraisal': 'Final Appraisal',
  'item.broker.final-appraisal.text': 'His head start is at least twice your best chain this run.',
  'item.broker.foreclosure': 'Foreclosure',
  'item.broker.foreclosure.text':
    'Each round you lose, he keeps one of your Superstitions for good.',

  // The Long Night: the menu, the table and the run's screens.
  'menu.night': 'The Long Night',
  'menu.nightHint': 'A roguelite run: Superstitions, the Counter, Omens and the Broker',
  'menu.nightResume': 'Resume · {where}',
  'menu.nightAt.counter': '{night} · at the Counter',
  'menu.nightAt.afterHigh': '{night} · after the High Table',
  'score.lucky': 'lucky!',
  'wild.free': 'Nothing (Rag Doll)',
  'ledger.title': 'The Ledger',
  'ledger.open': 'Ledger',
  'ledger.brass': 'Brass',
  'ledger.mantel': 'Mantel {n}/{slots}',
  'ledger.value': 'now {n}',
  'ledger.none': 'Nothing.',
  'ledger.stolen': 'Taken from this side: {items}',
  'ledger.curios': 'Curios',
  'ledger.appraisals': 'Appraisals',
  'ledger.level': 'level {n}',
  'ledger.rules': 'House Rules',
  'ledger.against': 'against {side}',
  'ledger.omens': 'Omens on the board',
  'ledger.statuses': 'Statuses',
  'ledger.curio.use': 'Use it',
  'ledger.curio.swap': 'Swap {card}',
  'ledger.curio.replayWith': 'Replay with {pawn}',
  'ledger.curio.copyEpitaph': "Copy {pawn}'s Epitaph",
  'ledger.curio.pull': 'Pull {pawn} to {square}',
  'ledger.curio.drawsFirst': 'This Curio draws a card: use it before your first move.',
  'ledger.seance.replay': 'Replay and end the turn',
  'ledger.seance.chain': 'Replay and keep the chain going',

  'night.title': 'The Long Night',
  'night.intro':
    'A roguelite run: win tables, earn brass, and build a Mantel of Superstitions that turns every chain into a fortune.',
  'night.stats': '{runs} runs · {wins} won · best chain {best} · deepest Night {night}',
  'night.classicRunActive': 'A classic campaign run is in progress: finish or abandon it first.',
  'night.pledge': 'Pledge',
  'night.locked': 'Locked',
  'night.unlockBy': 'Unlock: {how}',
  'night.wonTerm': 'won Term {n}',
  'night.term': 'Term',
  'night.termN': 'Term {n}',
  'night.heirloom': 'Heirloom',
  'night.noHeirloom': 'None',
  'night.newRun': 'Begin the Long Night',
  'night.daily': 'Daily Ledger · {date}',
  'night.dailyBest': 'Best today: {night}, chain {best}',
  'night.collection': 'The Ledger collection',
  'night.collectionCount': '{n} of {total} Superstitions seen',
  'night.features': '{n} of {total} features revealed',
  'night.historyItem':
    'Run {n} · {pledge}, Term {term} · reached {night} · best chain {best} · {outcome}',
  'night.nightN': 'Night {n}',
  'night.closingTime': 'Closing Time',
  'night.afterHours': 'After Hours, hour {n}',
  'night.table.small': 'Small Table',
  'night.table.big': 'Big Table',
  'night.table.high': 'High Table',
  'night.table.closing': 'Closing Time',
  'night.broker': 'The Broker',
  'night.brokerMan': "The Broker's man",
  'night.ante': 'head start {n}',
  'night.anteHint': 'Added to the opponent’s score at the start of every round.',
  'night.status.open': 'Open',
  'night.status.won': 'Won',
  'night.status.lost': 'Lost',
  'night.status.skipped': 'Skipped',
  'night.omensOnBoard': 'Omens: {omens}',
  'night.shrouded': 'a dark square',
  'night.oneRound': 'One round, played with a cut of {cut} cards from your run deck.',
  'night.fullMatch': 'A full match, played with your whole run deck.',
  'night.headStartHint': 'The opponent starts every round {n} points ahead. Beat that.',
  'night.sitDown': 'Sit down',
  'night.skip': 'Skip for {favor}',
  'night.abandonConfirm': 'Abandon this run? It counts as lost; no pawn is lost with it.',
  'night.runWon': 'The Broker is beaten',
  'night.runLost': 'The Long Night is over',
  'night.runSummary': 'You reached {night} with a best chain of {best} and ¤{brass}.',
  'night.hallmark': 'A Hallmark',
  'night.hallmarkHint':
    'Choose one Carving to keep for good: that pawn carries it into every run from now on.',
  'night.hallmarkSet': 'Make it a Hallmark',
  'night.hallmarkSkip': 'No Hallmark',
  'night.toAfterHours': 'Stay for After Hours',
  'night.anteWas': 'Their head start was {n} a round.',
  'night.bestChainHere': 'Your best chain here: {n}',
  'night.brassEarned':
    '¤{table} for the table, ¤{thrift} Thrift, ¤{interest} interest, ¤{items} from items, ¤{match} at the table',
  'night.brass.table': '¤{n} for winning the table',
  'night.brass.thrift': '¤{n} Thrift (cards left unplayed)',
  'night.brass.interest': '¤{n} interest',
  'night.brass.items': '¤{n} from items',
  'night.brass.match': '¤{n} earned at the table',

  'night.counter': 'The Counter',
  'night.counterHint':
    'Buy, sell and reorder before the next table. The Mantel counts left to right: additions first, multipliers last.',
  'night.tray': 'The tray',
  'night.ring': 'Ring the bell (¤{n}): new stock',
  'night.buy': 'Buy',
  'night.take': 'Take',
  'night.sold': 'Sold',
  'night.lots': 'Lots',
  'night.lotSize.normal': 'Lot',
  'night.lotSize.large': 'Large Lot',
  'night.lotSize.grand': 'Grand Lot',
  'night.open': 'Open',
  'night.lotPicks': 'take {n}',
  'night.closeLot': 'Leave the rest',
  'night.dome': 'Under the dome',
  'night.domeEmpty': 'The dome is empty this Night.',
  'night.backRoom': 'The Back Room',
  'night.pawnsCost': 'costs {n} pawn(s)',
  'night.strike': 'Strike the deal',
  'night.pawns': 'Pawns',
  'night.buyBack': 'Buy back {name} from the shelf',
  'night.hire': 'Hire a new pawn',
  'night.sellPawn': 'Sell (¤{n})',
  'night.melt': 'The scale',
  'night.meltOpen': 'Melt a card out of your deck (¤{n})',
  'night.meltCard': 'Melt it (¤{n})',
  'night.leave': 'Leave the Counter',
  'night.sell': 'Sell ¤{n}',
  'night.use': 'Use',
  'night.moveLeft': 'Move left',
  'night.moveRight': 'Move right',
  'night.deck': '{n} cards in the deck',
  'night.roster': '{n} pawns',
  'night.rosterTitle': 'Roster and Carvings',
  'night.bestChain': 'best chain {n}',
  'night.mantelHint': '(counted left to right)',
  'night.mantelEmpty': 'Your Mantel is bare. Superstitions go here.',
  'night.curios': 'Curios {n}/{slots}',
  'night.fixtures': 'Fixtures',
  'night.favors': 'Favors',
  'night.epitaphsHeld': '{n} Epitaph(s) for the next table.',
  'night.omensPlaced': 'Omens placed for the next table: {omens}.',
  'night.onLoan': 'On Loan',
  'night.loan': 'On Loan: ¤1 a table, worth nothing when sold.',
  'night.rotting': 'rots in {n}',
  'night.rotten': 'rotten',
  'night.bolted': 'Bolted',
  'night.seed': 'Seed: {seed}',
  'night.seedField': 'Run seed (optional)',
  'night.seedHint': 'Type a seed to play a run someone shared; leave empty for a new one.',
  'night.iou': 'IOU',
  'night.kind.superstition': 'Superstition',
  'night.kind.curio': 'Curio',
  'night.kind.appraisal': 'Appraisal',
  'night.kind.card': 'Card',
  'night.kind.carving': 'Carving',
  'night.kind.bargain': 'Bargain',
  'night.kind.route': 'Epitaph',
  'night.kind.omen': 'Omen',
  'night.kind.fixture': 'Fixture',
  'night.kind.detour': 'Detour',
  'night.rarity.common': 'Common',
  'night.rarity.uncommon': 'Uncommon',
  'night.rarity.rare': 'Rare',
  'night.rarity.legendary': 'Legendary',
  'night.offer.route': 'A route of the {name}',
  'night.offer.routeText': 'A ready-made Epitaph you bring to the next table.',
  'night.offer.omenPlace': 'You place it on the next board.',
  'night.target.carve': 'The pawn to carve',
  'night.target.lostPawn': 'A pawn lost this run',
  'night.target.noneLost': 'No pawn has been lost with a route yet.',
  'night.target.keep': 'The pawn that keeps both Carvings',
  'night.target.gone': 'The pawn that goes to the Broker',
  'night.target.donor': 'The pawn that gives its Carving (it goes to the Broker)',
  'night.target.carving': 'The Carving',
  'night.target.receiver': 'The pawn that takes it',
  'night.target.item': 'The Superstition to copy',
  'night.target.square': 'Choose a square (ranks 2–5)',
  'night.target.payWith': 'Pay with {n} named pawn(s): they go to the Broker',
  'night.target.keepSix': 'You must keep at least 6 pawns on the roster.',
  'night.bench.source': 'The card whose color the others take',
  'night.bench.cards': 'Choose up to {n} card(s)',
  'night.bench.up': 'Raise by 1',
  'night.bench.down': 'Lower by 1',

  'night.spoils': 'The Spoils',
  'night.spoilsHint': 'The High Table is yours. Take one thing from it.',
  'night.takeNothing': 'Take nothing',
  'night.detour': 'A Detour',
  'night.detourHint': 'On the way to the Counter, two doors are open. Choose one.',
  'night.go': 'Go in',
  'night.toCounter': 'On to the Counter',
  'night.skipDetour': 'Walk on to the Counter',
  'night.detourDone': 'Done.',
  'night.furnace.melt': 'Melt up to 3',
  'night.furnace.meltButton': 'Melt {n} card(s)',
  'night.furnace.stamp': 'Stamp one',
  'night.furnace.stampButton': 'Stamp it',
  'night.twins.pick': 'Choose two cards with the same face',
  'night.twins.fuse': 'Fuse them',
  'night.auction.bid': 'Your sealed bid (¤)',
  'night.auction.seal': 'Seal a bid of ¤{n}',
  'night.auction.empty': 'Nothing is up for auction tonight.',
  'night.auction.won': 'Your bid wins. The regulars bid {bids}.',
  'night.auction.lost': 'Outbid: the regulars bid {bids}. Your brass stays with you.',
  'night.confess.plain': 'Confess: every IOU is forgiven',
  'night.confess.rot': 'Confess and pay ¤8: lift the rot',
  'night.chart.pick': 'Chart it',
  'night.chart.button': 'Chart them · {n}/3',
  'night.chart.where':
    'Charted, it marks squares at every table of the next Night, in pairs: one in your half of the board and one in theirs.',
  'night.puzzle.target': 'Score {n} or more with one chain this turn.',
  'night.puzzle.open': 'Open the Puzzle Box',
  'night.puzzle.title': 'The Puzzle Box · score {n}',
  'night.puzzle.done': 'Done',
  'night.puzzle.box': 'The Puzzle Box',
  'night.puzzle.solved': 'Solved! The box opens.',
  'night.puzzle.failed': 'The box stays shut.',
  // The Long Night: the redesigned screens.
  'ledger.openLabel': 'The Ledger: ¤{n}, your Mantel and Curios',
  'ledger.yourMantel': 'Your Mantel',
  'ledger.theirMantel': "{name}'s Mantel",
  'ledger.curiosHint': 'Use them during your turn.',
  'ledger.thisTable': 'This table',
  'ledger.curio.choose': 'Choose how to use it:',
  'ledger.tableTip':
    'Your brass, Mantel and Curios are here. Tap any pawn or marked square to see what it does.',
  'night.howItWorks': 'How it works',
  'night.pledgeHint': 'Your starting kit. More unlock as you play.',
  'night.termHint': 'Higher Terms are harder.',
  'night.options': 'More options',
  'night.optionsHint': 'Lineup, Heirloom, seed',
  'night.lineupChoose': 'Choose {n} pawns',
  'night.seedPlaceholder': 'Leave empty for a new run',
  'night.yourLedger': 'Your Ledger',
  'night.seeCollection': 'Collection',
  'night.collectionHint': 'Every Superstition you have seen. The rest are still to be found.',
  'night.dailyShort': 'Daily',
  'night.tableWaiting': 'A table is waiting for you.',
  'night.play': 'Play',
  'night.skipShort': 'Skip',
  'night.skipText':
    'Skipping gives up this table: no brass and no Counter after it, and word reaches the High Table, whose head start grows by {pct}%. The Favor is yours and the next table comes up.',
  'night.oneRoundShort': 'one round',
  'night.fullMatchShort': 'best of three rounds',
  'night.headStart': 'Head start',
  'night.perRound': 'a round',
  'night.raisedBy': '{n} skipped',
  'night.raisedText':
    'Tables skipped tonight: {n}. Word of each reached this table, and its head start grew by {pct}% for every one.',
  'night.bargainCost.mortgage': 'Two IOUs',
  'night.bargainCost.burnt-ledger': 'All your brass',
  'night.bargainCost.changeling': '¤5',
  'night.bargainCost.black-wild': 'A larger head start',
  'night.bargainCost.brokers-hand': 'Every head start, +25%',
  'night.mantel': 'The Mantel',
  'night.rate.title': 'Rate card',
  'night.rate.hint': 'What your Appraisals add',
  'night.rate.none': 'No pattern',
  'night.rate.capture': 'Each capture',
  'night.rate.crown': 'Each crowning',
  'night.rate.landing': 'Each landing',
  'night.rate.epitaph': 'Epitaph replays',
  'broker.line.counter1': 'Everything here has belonged to someone.',
  'broker.line.counter2': 'Take your time. The night is long.',
  'broker.line.counter3': 'Brass spends the same in any hand.',
  'broker.line.ringThrice': 'Indecision is an expensive habit.',
  'broker.line.sellPawn': "I'll keep them warm for you.",
  'broker.line.buyBack': 'Some things come back. For a price.',
  'broker.line.leaveEmpty': "Empty pockets. Good. Now you'll play carefully.",
  'broker.line.rare': "Ah. Don't tell the others I showed you this one.",
  'broker.line.backRoom': 'Not everything here is for sale. Everything has a price.',
  'broker.line.firstCounter': "Brass for goods. Pawns for better goods. I'm generous like that.",
  'broker.line.closing': 'Closing time. Let us settle up.',
  'broker.line.runLost': "They'll keep. They always do.",
  'broker.line.closingLost': 'Dawn already. Take them, then.',
  'placard.turns': '{side}: turn {used} of {n} this round',
  'placard.darkInLong': 'The other half of the board goes dark in {n} turns',
  'placard.darkNextLong': 'The other half of the board goes dark next turn',
  'notice.lastTurn': 'Your last turn this round: {rule} allows {n}. After it you pass.',
  'placard.open': 'The rules of this table',
  'night.title.first': 'The First Night',
  'night.title.second': 'The Second Night',
  'night.title.third': 'The Third Night',
  'night.hour.first': 'The First Hour',
  'night.hour.second': 'The Second Hour',
  'night.hour.third': 'The Third Hour',
  'night.hour.fourth': 'The Fourth Hour',
  'night.hour.fifth': 'The Fifth Hour',
  'night.hour.sixth': 'The Sixth Hour',
  'night.hour.seventh': 'The Seventh Hour',
  'night.hour.eighth': 'The Eighth Hour',
  'night.interestNext': 'Interest at the next table: +¤{n}',
  'hub.hisShelf': 'His shelf: {n} of your pawns',
  'item.prev': 'The one before',
  'item.next': 'The next one',
  'item.count': '{n} of {of}',
  'hub.prev': 'The Pledge before',
  'hub.next': 'The next Pledge',
  'hub.bell': 'The bell',
  'hub.daily': 'The Daily Ledger',
  'hub.dailyText': "Today's run for everyone: {pledge}, Term {term}, on the same seed.",
  'hub.dailyBegin': "Begin today's run",
  'ledgerBook.title': 'The Ledger',
  'ledgerBook.page.superstitions': 'Superstitions',
  'ledgerBook.page.curios': 'Curios',
  'ledgerBook.page.appraisals': 'Appraisals',
  'ledgerBook.page.carvings': 'Carvings',
  'ledgerBook.page.bargains': 'Bargains',
  'ledgerBook.page.fixtures': 'Fixtures',
  'ledgerBook.page.his': 'His collection',
  'ledgerBook.found': '{n} of {total} found',
  'ledgerBook.hisEmpty': 'He holds none of yours. Yet.',
  'ledgerBook.record': '{tables} tables · {captures} captures · best chain {best}',
  'ledgerBook.how.counter': 'Still to be found at the Counter.',
  'ledgerBook.how.carving': "At the Carver's Lot, the Workbench, or a Carver's bench.",
  'ledgerBook.how.backRoom': 'Behind the curtain of the Back Room, from the Second Night.',
  'ledgerBook.how.monochromeRounds': 'Play a whole round in one color.',
  'ledgerBook.how.epitaphReplays': 'Replay {n} Epitaphs.',
  'ledgerBook.how.pawnedInATable': 'Pawn {n} pawns at one table.',
  'ledgerBook.how.ladders': 'Score a Ladder.',
  'ledgerBook.how.mostBrass': 'Hold ¤{n} at once.',
  'ledgerBook.how.fullCircles': 'Make a Full Circle.',
  'ledgerBook.how.capturesInARound': 'Capture {n} pawns in one round.',
  'ledgerBook.how.patternsScored': 'Score {n} patterns.',
  'ledgerBook.how.chancesHit': 'Win {n} chances.',
  'ledgerBook.how.wins': 'Win a Long Night.',
  'night.favorShort': 'Favor',
  'night.won': 'Won',
  'night.oneRoundText': 'One round: the higher score wins the table. You play a cut of your deck.',
  'night.fullMatchText': 'A full match: win two rounds of three to win the table.',
  'night.fieldingHint':
    'Tap a pawn, then a square of your home row. Hold a pawn to read its record. Empty squares are filled by stand-ins.',
  'receipt.table': 'Table won',
  'receipt.thrift': 'Thrift, cards kept',
  'receipt.interest': 'Interest on ¤{n}',
  'receipt.items': 'Your things',
  'receipt.match': 'At the table',
  'receipt.total': 'Paid to you',
  'receipt.score': 'You {you} · them {them}',
  'receipt.nowHave': 'You now hold ¤{n}',
  'receipt.toCounter': 'To the Counter',
  'receipt.toSpoils': 'To the Spoils',
  'night.evenStart': 'An even start',
  'night.anteValue': 'They start {n} points ahead',
  'night.headStartText':
    'The opponent starts every round this many points ahead. To win the round you must score more than them, head start included.',
  'night.kind.rule': 'House Rule',
  'night.kind.trick': 'Trick',
  'night.rule.how': 'This table plays with this rule.',
  'night.omensCount': '{n} Omens',
  'night.omensText':
    'Omens are marked squares. A pawn that lands on one sets it off. Tap a marked square during the table to read it.',
  'night.lineup': 'Lineup',
  'night.change': 'Change',
  'night.lineupHint': 'Which pawn starts on each file. Pawns left out wait as reserves.',
  'night.kind.favor': 'Favor',
  'night.inventory': 'Your things',
  'night.counterEyebrow': 'Between tables',
  'night.counterLead': 'Tap anything to see what it does.',
  'night.mantelTitle': 'Mantel {n}/{slots}',
  'night.mantelPos': 'Position {n} on your Mantel. Superstitions count left to right.',
  'night.emptySlot': 'An empty slot',
  'night.ringShort': 'Restock ¤{n}',
  'night.ringHint': 'Ring the bell for new stock. It costs ¤1 more each time this visit.',
  'night.buyFor': 'Buy · ¤{n}',
  'night.openFor': 'Open · ¤{n}',
  'night.free': 'Free',
  'night.lotsHint': 'Sealed packs: open one, then choose.',
  'night.lot.how': 'Opening it shows {show}; you keep {take}.',
  'night.lotPick': 'Choose {n}.',
  'night.moreTitle': 'More',
  'night.moreHint': 'Bargains, pawns, melting cards',
  'night.backRoomHint': 'Strong deals, paid with pawns. A pawn you give goes to the Broker.',
  'night.pawnsHint': 'Hire or buy back pawns; tap a named one to sell it. You need six to play.',
  'night.sellPawnNamed': 'Sell {name}',
  'night.meltHint': 'Remove a card from your deck for good.',
  'night.why.brass': 'Not enough brass.',
  'night.why.mantel': 'Your Mantel is full: sell a Superstition first.',
  'night.why.pockets': 'Your Curio pockets are full: use or sell one first.',
  'night.curiosHint': 'One use each',
  'night.noneYet': 'None yet.',
  'night.afterHigh': 'After the High Table',
  'night.chart.hint':
    'An Omen marks a square; a pawn that lands there sets it off, yours or theirs. Tap three to chart them; hold one to read it in full.',
  'night.card.plain': 'A card for your deck.',
  'night.card.wild': 'A Wild for your deck: it can be played as any face, paid for with a pawn.',
  'night.card.iou': 'IOU: it can’t be played and takes a hand slot; Recovering it removes it.',
  'night.curio.table': 'A Table Curio: use it once, during your turn at a table.',
  'night.curio.bench': 'A Bench Curio: use it once, between tables, on your deck.',
  'night.appraisal.how': 'An Appraisal raises a pattern’s level for the rest of the run.',
  'night.carving.how': 'A Carving goes on one pawn of your roster, for the rest of the run.',
  'night.fixture.how': 'A Fixture improves the Counter or your tables for the rest of the run.',
  'night.boltedText': 'Bolted: it can’t be sold.',
  'night.rottingText': 'Rotting: it stops working after 5 tables.',
  'night.them': 'Them',
  'night.earned': 'Brass earned',
  'night.earnedHint': 'Spend it at the Counter.',
  'night.nowHave': 'You have',
  'night.recap': 'The run',

  'nightGuide.title': 'The Long Night',
  'nightGuide.done': 'Begin',
  'nightGuide.1.title': 'One long night',
  'nightGuide.1.text':
    'Three Nights of three tables, then Closing Time against the Broker. Lose a table and the run is over.',
  'nightGuide.2.title': 'Beat the head start',
  'nightGuide.2.text':
    'Each opponent starts every round ahead. Small and Big tables are one round; the High Table is a full match.',
  'nightGuide.3.title': 'Earn brass',
  'nightGuide.3.text':
    'Winning pays brass (¤): for the table, for cards you didn’t need, and interest on what you keep.',
  'nightGuide.4.title': 'Spend it at the Counter',
  'nightGuide.4.text':
    'Between tables, buy Superstitions for your Mantel, Curios, Appraisals and cards. Tap anything to read it.',
  'nightGuide.5.title': 'Order matters',
  'nightGuide.5.text':
    'A chain scores P × M. Your Superstitions add to it left to right, so put multipliers last.',
  'nightGuide.6.title': 'Learn as you go',
  'nightGuide.6.text':
    'New things appear the further you get. Tap the ⓘ at the top to see this again.',

  'gloss.p': 'P',
  'gloss.p.text': 'Points: the first number of a score.',
  'gloss.m': 'M',
  'gloss.m.text': 'Multiplier: a chain scores P × M.',
  'gloss.brass': 'Brass (¤)',
  'gloss.brass.text': 'The run’s money, earned by winning tables and spent at the Counter.',
  'gloss.chain': 'Chain',
  'gloss.chain.text': 'The cards one pawn plays in a turn, scored together.',
  'gloss.link': 'Link',
  'gloss.link.text': 'One card of a chain. Each link adds 1 M.',
  'gloss.landing': 'Landing',
  'gloss.landing.text': 'A square a move ends on. New squares in a chain score P.',
  'gloss.capture': 'Capture',
  'gloss.capture.text': 'Landing on an enemy pawn takes it off the board.',
  'gloss.crown': 'Crowning',
  'gloss.crown.text': 'Reaching the far row crowns a pawn: it may then move backward too.',
  'gloss.pattern': 'Pattern',
  'gloss.pattern.text': 'A shape a chain’s cards make (Ladder, Echo, Streak…), worth extra M.',
  'gloss.ladder': 'Ladder',
  'gloss.ladder.text': 'Three cards of one color, 1 then 2 then 3.',
  'gloss.echo': 'Echo',
  'gloss.echo.text': 'Two or more cards with the same number.',
  'gloss.streak': 'Streak',
  'gloss.streak.text': 'Two or more cards of the same color.',
  'gloss.stair': 'Stair',
  'gloss.stair.text': 'A hidden pattern: 1, 2, 3 in mixed colors.',
  'gloss.prism': 'Prism',
  'gloss.prism.text': 'A hidden pattern: three or more cards, every color different.',
  'gloss.mirror': 'Mirror',
  'gloss.mirror.text': 'A hidden pattern: the first and last cards have the same face.',
  'gloss.fullCircle': 'Full Circle',
  'gloss.fullCircle.text':
    'The chain pawn ends on the square it started from, by a route that is not one straight line: out and straight back does not count. An uncrowned pawn must crown in the chain to come back. +2 M.',
  'gloss.epitaph': 'Epitaph',
  'gloss.epitaph.text': 'A fallen pawn’s route, kept to be replayed later for points.',
  'gloss.inscription': 'Inscription',
  'gloss.inscription.text': 'The route a pawn remembers: its Epitaph if it is captured.',
  'gloss.wild': 'Wild',
  'gloss.wild.text': 'A card played as any face; you pay for it with one of your pawns.',
  'gloss.collateral': 'Collateral',
  'gloss.collateral.text': 'The pawn you give up to play a Wild.',
  'gloss.pawned': 'Pawned',
  'gloss.pawned.text': 'Given up as a Wild’s collateral.',
  'gloss.recover': 'Recover',
  'gloss.recover.text': 'Put a card back under your deck to draw a new one.',
  'gloss.homeRow': 'Home row',
  'gloss.homeRow.text': 'Your back row, where your pawns can’t be captured.',
  'gloss.round': 'Round',
  'gloss.round.text': 'Play until both sides pass; the higher score wins the round.',
  'gloss.table': 'Table',
  'gloss.table.text': 'One match of the run.',
  'gloss.headStart': 'Head start',
  'gloss.headStart.text': 'Points the opponent starts every round with.',
  'gloss.mantel': 'Mantel',
  'gloss.mantel.text': 'Where your Superstitions sit. They count left to right.',
  'gloss.superstition': 'Superstition',
  'gloss.superstition.text': 'A lasting item on your Mantel that changes how you score.',
  'gloss.curio': 'Curio',
  'gloss.curio.text': 'An item you use once.',
  'gloss.appraisal': 'Appraisal',
  'gloss.appraisal.text': 'A level for a pattern or feat, raised for the run.',
  'gloss.stamp': 'Stamp',
  'gloss.stamp.text': 'A mark on a card that triggers when the card is played.',
  'gloss.finish': 'Finish',
  'gloss.finish.text': 'Foil, Gilt, Pearl or Ghost: a bonus on a card or Superstition.',
  'gloss.carving': 'Carving',
  'gloss.carving.text': 'An ability carved into one pawn for the run.',
  'gloss.omen': 'Omen',
  'gloss.omen.text': 'A marked square that does something when a pawn lands on it.',
  'gloss.shrouded': 'Shrouded',
  'gloss.shrouded.text': 'An Omen nobody can see until something lands on it.',
  'gloss.houseRule': 'House Rule',
  'gloss.houseRule.text': 'A rule a table adds, usually against you.',
  'gloss.trick': 'Trick',
  'gloss.trick.text': 'A card that counts as a link but moves nothing: it does its effect instead.',
  'gloss.thrift': 'Thrift',
  'gloss.thrift.text': 'Brass for cards you still hold when you win.',
  'gloss.interest': 'Interest',
  'gloss.interest.text': '¤1 for every ¤5 you hold after a win.',
  'gloss.favor': 'Favor',
  'gloss.favor.text': 'A reward for skipping a table.',
  'gloss.warded': 'Warded',
  'gloss.warded.text': 'Can’t be captured.',
  'gloss.hasted': 'Hasted',
  'gloss.hasted.text': 'Its next Red or Blue move may go one square shorter or longer.',
  'gloss.kindled': 'Kindled',
  'gloss.kindled.text': 'Its chains score more P, stacking.',
  'gloss.phased': 'Phased',
  'gloss.phased.text': 'Its slides pass through pieces.',
  'gloss.gilded': 'Gilded',
  'gloss.gilded.text': 'Earns ¤1 when it captures or crowns.',
  'gloss.pinned': 'Pinned',
  'gloss.pinned.text': 'Can’t move or pay for a Wild.',
  'gloss.exposed': 'Exposed',
  'gloss.exposed.text': 'Can be captured even on its home row.',
  'gloss.cursed': 'Cursed',
  'gloss.cursed.text': 'Its chains score less.',
  'gloss.smoldering': 'Smoldering',
  'gloss.smoldering.text': 'Burns out when its fuse runs down.',
  'gloss.hollow': 'Hollow',
  'gloss.hollow.text': 'Leaves no Epitaph when captured.',
  'gloss.heavy': 'Heavy',
  'gloss.heavy.text': 'Red and Blue cards move it exactly one square.',
  'gloss.inspired': 'Inspired',
  'gloss.inspired.text': 'Your next chain gets +2 M.',
  'gloss.reserve': 'Reserve',
  'gloss.reserve.text': 'A roster pawn not on the board; it can pay for a Wild.',
  'gloss.broker': 'The Broker',
  'gloss.broker.text': 'The dealer of the Long Night. Pawns you lose go to him.',
  'counter.after': 'after the {table}',
  'counter.workbench': 'The Workbench',
  'counter.shelf': 'The shelf',
  'counter.interest': 'Interest next table',
  'counter.lastBest': 'Your best chain last table',
  'counter.picksLeft': '{n} to take',
  'counter.backRoomEmpty': 'The table is bare. Nothing more tonight.',
  'counter.backOut': 'Step back out',
  'counter.benchEmpty': 'Choose a card from your deck to lay on the bench.',
  'counter.hire': 'Hire a pawn · ¤{n}',
  'night.pawnCost': 'Costs {n} of your pawns',
  'night.round.one': 'Round one',
  'night.round.two': 'Round two',
  'night.round.three': 'Round three',
  'night.round.four': 'Round four',
  'night.round.five': 'Round five',
  'night.walkOn': 'Walk on',
  'night.contract': 'His House Rule',
  'end.dawn': 'Dawn',
  'night.nextTable': 'Next',
  'night.skipFor': 'Skip for a Favor ({favor}); the High Table starts {pct}% higher',
  'night.skipForN': 'Skip for a Favor ({favor}): High Table head start {now} → {then}',
  'night.skipResult': 'Tonight’s High Table would start {then} ahead each round, instead of {now}.',
  'night.skipResultWaiver':
    'Tonight’s High Table would start {then} ahead each round, instead of {now}: {pct}% more for the skip, then halved by the Toll Waiver.',
  'end.share': 'Share the card',
  'share.cardTitle': 'Your card',
  'share.cardShare': 'Share the picture',
  'share.cardFailed': 'The picture could not be made here.',
  'pawn.carved': 'carved with {list}',
  'night.hourShort': 'Hour {n}',
  'end.leaveAtDawn': 'Leave at dawn',
  'end.newRun': 'New run',
  'end.reached': 'As far as {night}',
  'end.bestChain': 'Best chain',
  'end.firedMost': 'Fired most, {n} times',
  'end.lastTable': 'The last table',
  'end.nextTray': 'It was in the next tray.',
  'end.lostPawns': 'Lost to him',
  'end.homePawns': 'Brought home',
  'night.moment.stolen': 'Taken from your Mantel: {name}',
  'night.moment.foreclosed': 'Foreclosed: {name}',
  'night.contract.blindEye':
    'And he blinds one of your eyes: half the board is dark, then the other.',
  'night.contract.best': 'Your best chain, {n}',
  'night.contract.ante': 'Ante',
  'spoils.reclaim': 'They come back to you',
  'spoils.recruit': 'A new pawn joins you',
} as const;

export type MessageKey = keyof typeof en;
